// LeadEconomics™ Professional Assessment Question Bank V2
// 200+ questions across LeadEconomics Core Dimensions
// Randomized 85 questions per assessment
// Multi-format: Situational Judgement, Forced Choice, Ranking, Scenario Reaction, Reflection

export const PROFESSIONAL_QUESTIONS_V2 = [
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 1: STRATEGIC THINKING & DECISION QUALITY (35 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Situational Judgement Questions
  {
    id: 'pro_strat_1',
    phase: 'strategic_decision',
    type: 'situational_judgement',
    question: 'Your organization faces a choice between pursuing a high-risk market opportunity or consolidating current operations. Resources allow only one path.',
    subtitle: 'Rate your agreement with this approach:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would gather comprehensive market data before committing to either direction.',
    weights: {
      strongly_disagree: { impulsive: 2, risk_seeking: 1 },
      disagree: { intuition: 1, decisive: 1 },
      agree: { analytical: 1, validation_discipline: 1 },
      strongly_agree: { analytical: 2, strategic_thinking: 1, validation_discipline: 1 }
    }
  },
  {
    id: 'pro_strat_2',
    phase: 'strategic_decision',
    type: 'situational_judgement',
    question: 'A trusted advisor strongly recommends a strategy that contradicts your own analysis. The advisor has deep domain expertise.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would seriously reconsider my position and seek to understand their perspective.',
    weights: {
      strongly_disagree: { overconfidence: 2, rigid: 1 },
      disagree: { conviction: 1, independence: 1 },
      agree: { openness: 1, learning_agility: 1 },
      strongly_agree: { intellectual_humility: 2, collaboration: 1 }
    }
  },
  {
    id: 'pro_strat_3',
    phase: 'strategic_decision',
    type: 'situational_judgement',
    question: 'Your team presents three viable solutions to a critical problem. Each has different risk profiles and resource requirements.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I prefer to run small pilots of multiple options before full commitment.',
    weights: {
      strongly_disagree: { decisive: 2, action_bias: 1 },
      disagree: { conviction: 1, efficiency: 1 },
      agree: { measured_risk: 1, learning_agility: 1 },
      strongly_agree: { experimentation: 2, prudence: 1 }
    }
  },
  
  // Forced Choice Questions
  {
    id: 'pro_strat_4',
    phase: 'strategic_decision',
    type: 'forced_choice',
    question: 'When making a significant business decision, which factor weighs most heavily?',
    subtitle: 'Select the option closest to your natural approach',
    options: [
      { id: 'a', text: 'Quantitative data and financial projections', weight: { analytical: 2, data_driven: 1, structured_thinking: 1 } },
      { id: 'b', text: 'Pattern recognition from past experiences', weight: { intuition: 2, experience: 1, pattern_recognition: 1 } },
      { id: 'c', text: 'Input from diverse stakeholders and experts', weight: { collaboration: 2, inclusion: 1, validation_discipline: 1 } }
    ]
  },
  {
    id: 'pro_strat_5',
    phase: 'strategic_decision',
    type: 'forced_choice',
    question: 'When your initial strategy shows signs of underperformance, you typically:',
    subtitle: 'Choose your most natural response',
    options: [
      { id: 'a', text: 'Analyze root causes and adjust the approach incrementally', weight: { analytical: 1, adaptability: 1, persistence: 1 } },
      { id: 'b', text: 'Maintain course with conviction—results take time', weight: { conviction: 1, persistence: 2, bias_overconfidence: 1 } },
      { id: 'c', text: 'Quickly pivot to an alternative approach', weight: { adaptability: 2, agility: 1, decision_velocity: 1 } }
    ]
  },
  {
    id: 'pro_strat_6',
    phase: 'strategic_decision',
    type: 'forced_choice',
    question: 'In evaluating a major opportunity, you prioritize:',
    subtitle: 'Select your primary consideration',
    options: [
      { id: 'a', text: 'Potential upside and growth opportunity', weight: { opportunistic: 2, growth: 1, entrepreneurial: 1 } },
      { id: 'b', text: 'Risk mitigation and downside protection', weight: { prudence: 2, risk_aware: 1, conservative: 1 } },
      { id: 'c', text: 'Strategic alignment with long-term vision', weight: { strategic_thinking: 2, long_term: 1, values_driven: 1 } }
    ]
  },
  
  // Scenario Reaction Questions
  {
    id: 'pro_strat_7',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'Your organization discovers a competitor is developing a product that could disrupt your core business within 18 months.',
    subtitle: 'What is your immediate response?',
    options: [
      { id: 'a', text: 'Convene strategic leadership to assess and develop counter-measures', weight: { strategic_thinking: 1, initiative: 1, leadership: 1 } },
      { id: 'b', text: 'Commission deep competitive intelligence before reacting', weight: { analytical: 1, validation_discipline: 1, prudence: 1 } },
      { id: 'c', text: 'Accelerate our innovation roadmap to maintain differentiation', weight: { innovation: 1, confidence: 1, execution_intensity: 1 } },
      { id: 'd', text: 'Explore partnership or acquisition to neutralize the threat', weight: { strategic_thinking: 2, resourcefulness: 1 } }
    ]
  },
  {
    id: 'pro_strat_8',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'You have been offered two growth paths: organic expansion requiring patience, or acquisition requiring significant capital.',
    subtitle: 'Which approach appeals more to you?',
    options: [
      { id: 'a', text: 'Organic expansion—sustainable and controlled growth', weight: { patience: 1, stability: 1, prudence: 1 } },
      { id: 'b', text: 'Acquisition—faster market capture justifies the risk', weight: { ambitious: 1, decisive: 1, risk_tolerance: 1 } },
      { id: 'c', text: 'Hybrid approach—organic growth while scouting acquisition targets', weight: { adaptability: 1, strategic_thinking: 1, balanced: 1 } },
      { id: 'd', text: 'Depends entirely on the specific opportunity and timing', weight: { contextual: 1, analytical: 1, flexibility: 1 } }
    ]
  },
  {
    id: 'pro_strat_9',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'A decision you championed has clearly failed. The organization has invested significant resources based on your recommendation.',
    subtitle: 'How do you handle this situation?',
    options: [
      { id: 'a', text: 'Publicly acknowledge the failure and lead the course correction', weight: { integrity: 2, accountability: 1, transparency: 1 } },
      { id: 'b', text: 'Analyze what went wrong and implement improvements quietly', weight: { pragmatic: 1, learning: 1, discreet: 1 } },
      { id: 'c', text: 'Share accountability with the team who executed the decision', weight: { shared_accountability: 1, delegation: 1 } },
      { id: 'd', text: 'Focus on salvaging value rather than dwelling on failure attribution', weight: { forward_focus: 1, pragmatic: 1, solution: 1 } }
    ]
  },
  {
    id: 'pro_strat_10',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'Your organization must choose between entering a new market segment or deepening presence in existing markets.',
    subtitle: 'What guides your decision?',
    options: [
      { id: 'a', text: 'Market analysis and competitive dynamics of each option', weight: { analytical: 2, data_driven: 1 } },
      { id: 'b', text: 'Organizational capabilities and readiness assessment', weight: { self_aware: 1, realistic: 1, prudence: 1 } },
      { id: 'c', text: 'Long-term strategic vision and growth trajectory', weight: { strategic_thinking: 2, visionary: 1 } },
      { id: 'd', text: 'Input from customers and stakeholders across both options', weight: { customer_centric: 1, inclusion: 1, collaboration: 1 } }
    ]
  },
  
  // Reflection Questions
  {
    id: 'pro_strat_11',
    phase: 'strategic_decision',
    type: 'reflection',
    question: 'Which statement best describes how you approach complex decisions?',
    subtitle: 'Choose the most accurate description of your style',
    options: [
      { id: 'a', text: 'I rely primarily on logical analysis and data', weight: { analytical: 2, rational: 1, structured_thinking: 1 } },
      { id: 'b', text: 'I balance logic with intuition and experience', weight: { balanced: 1, intuition: 1, analytical: 1 } },
      { id: 'c', text: 'I trust my instincts developed through experience', weight: { intuition: 2, experiential: 1, confidence: 1 } },
      { id: 'd', text: 'I prefer consulting others before deciding', weight: { collaborative: 2, inclusion: 1, validation: 1 } }
    ]
  },
  {
    id: 'pro_strat_12',
    phase: 'strategic_decision',
    type: 'reflection',
    question: 'When facing uncertainty, your natural tendency is to:',
    subtitle: 'Select what feels most true for you',
    options: [
      { id: 'a', text: 'Wait for more information before acting', weight: { cautious: 1, analytical: 1, risk_averse: 1 } },
      { id: 'b', text: 'Make the best decision with available information', weight: { decisive: 1, pragmatic: 1, action_oriented: 1 } },
      { id: 'c', text: 'Test multiple options simultaneously', weight: { experimentation: 1, adaptability: 1, learning_agility: 1 } },
      { id: 'd', text: 'Seek counsel from trusted advisors', weight: { collaborative: 1, humility: 1, network: 1 } }
    ]
  },
  {
    id: 'pro_strat_13',
    phase: 'strategic_decision',
    type: 'reflection',
    question: 'Your approach to risk in decision-making is best described as:',
    subtitle: 'Choose your natural orientation',
    options: [
      { id: 'a', text: 'Calculated risk-taking based on thorough analysis', weight: { measured_risk: 2, analytical: 1, strategic_thinking: 1 } },
      { id: 'b', text: 'Conservative—prefer proven approaches', weight: { conservative: 2, risk_averse: 1, stability: 1 } },
      { id: 'c', text: 'Bold—willing to take significant risks for significant rewards', weight: { risk_tolerance: 2, ambitious: 1, entrepreneurial: 1 } },
      { id: 'd', text: 'Contextual—risk appetite varies by situation', weight: { adaptable: 1, contextual: 1, flexible: 1 } }
    ]
  },
  
  // Additional Strategic Questions
  {
    id: 'pro_strat_14',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'Your Board requests a strategic plan with aggressive targets. Your team believes the targets are unrealistic.',
    subtitle: 'How do you proceed?',
    options: [
      { id: 'a', text: 'Present a candid assessment to the Board with achievable alternatives', weight: { integrity: 1, transparency: 1, courage: 1 } },
      { id: 'b', text: 'Accept the targets and drive the team to stretch performance', weight: { ambitious: 1, challenge: 1, high_expectation: 1 } },
      { id: 'c', text: 'Negotiate middle ground between Board expectations and team reality', weight: { diplomacy: 1, negotiation: 1, pragmatic: 1 } },
      { id: 'd', text: 'Structure milestones that demonstrate progress toward stretched goals', weight: { strategic_thinking: 1, execution_focus: 1, balanced: 1 } }
    ]
  },
  {
    id: 'pro_strat_15',
    phase: 'strategic_decision',
    type: 'forced_choice',
    question: 'When setting strategic priorities, you tend to focus on:',
    subtitle: 'Select your primary orientation',
    options: [
      { id: 'a', text: 'A few critical initiatives with full resource commitment', weight: { focus: 2, execution_intensity: 1, prioritization: 1 } },
      { id: 'b', text: 'Multiple initiatives to diversify risk and opportunity', weight: { diversification: 1, portfolio: 1, hedging: 1 } },
      { id: 'c', text: 'Flexible priorities that can adapt to changing conditions', weight: { adaptability: 2, agility: 1, responsive: 1 } }
    ]
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 2: LEADERSHIP INFLUENCE & RELATIONSHIP ORIENTATION (35 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  {
    id: 'pro_lead_1',
    phase: 'leadership_influence',
    type: 'situational_judgement',
    question: 'A high-performing team member consistently undermines your decisions in group settings while delivering excellent results.',
    subtitle: 'Rate your agreement with this approach:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would address the behavior directly in a private conversation.',
    weights: {
      strongly_disagree: { avoidant: 2, passive: 1 },
      disagree: { tolerant: 1, pragmatic: 1 },
      agree: { direct: 1, assertive: 1 },
      strongly_agree: { direct: 2, assertive: 1, leadership: 1 }
    }
  },
  {
    id: 'pro_lead_2',
    phase: 'leadership_influence',
    type: 'situational_judgement',
    question: 'Your team is demoralized after a significant organizational setback that was beyond their control.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would acknowledge the setback openly and focus the team on what we can control.',
    weights: {
      strongly_disagree: { detached: 1, avoidant: 1 },
      disagree: { minimizing: 1, deflecting: 1 },
      agree: { transparent: 1, supportive: 1 },
      strongly_agree: { empathetic: 2, resilience: 1, transparency: 1 }
    }
  },
  {
    id: 'pro_lead_3',
    phase: 'leadership_influence',
    type: 'forced_choice',
    question: 'When building influence with a new stakeholder group, you typically:',
    subtitle: 'Select your primary approach',
    options: [
      { id: 'a', text: 'Demonstrate expertise and deliver quick wins to build credibility', weight: { results_driven: 1, competence: 1, execution: 1 } },
      { id: 'b', text: 'Invest time in understanding their priorities and concerns first', weight: { empathy: 1, relationship: 1, listening: 1 } },
      { id: 'c', text: 'Identify shared interests and mutual value opportunities', weight: { strategic_thinking: 1, collaborative: 1, win_win: 1 } }
    ]
  },
  {
    id: 'pro_lead_4',
    phase: 'leadership_influence',
    type: 'forced_choice',
    question: 'In leading a diverse team through change, your primary focus is:',
    subtitle: 'Choose your leadership emphasis',
    options: [
      { id: 'a', text: 'Communicating a compelling vision that inspires commitment', weight: { visionary: 2, inspirational: 1, communication: 1 } },
      { id: 'b', text: 'Creating structured plans that reduce anxiety and uncertainty', weight: { structured: 1, stability: 1, execution_discipline: 1 } },
      { id: 'c', text: 'Individually engaging key influencers to build momentum', weight: { influence: 1, stakeholder_mgmt: 1, political_savvy: 1 } }
    ]
  },
  {
    id: 'pro_lead_5',
    phase: 'leadership_influence',
    type: 'scenario',
    question: 'Two of your direct reports have an ongoing conflict that is affecting team performance.',
    subtitle: 'How do you address this situation?',
    options: [
      { id: 'a', text: 'Meet with each individually to understand perspectives, then facilitate resolution', weight: { mediation: 1, empathy: 1, conflict_resolution: 1 } },
      { id: 'b', text: 'Set clear expectations that personal conflicts cannot impact work', weight: { direct: 1, standards: 1, accountability: 1 } },
      { id: 'c', text: 'Allow them to work it out themselves unless it escalates further', weight: { delegation: 1, autonomy: 1, hands_off: 1 } },
      { id: 'd', text: 'Restructure responsibilities to minimize their interaction', weight: { pragmatic: 1, structural: 1, avoidant: 1 } }
    ]
  },
  {
    id: 'pro_lead_6',
    phase: 'leadership_influence',
    type: 'scenario',
    question: 'You need to deliver difficult feedback to a senior colleague who is defensive and has significant organizational influence.',
    subtitle: 'How do you approach this conversation?',
    options: [
      { id: 'a', text: 'Be direct and factual, focusing on specific behaviors and impacts', weight: { direct: 2, courageous: 1, integrity: 1 } },
      { id: 'b', text: 'Frame feedback as observations and questions to encourage self-reflection', weight: { diplomatic: 1, coaching: 1, tactful: 1 } },
      { id: 'c', text: 'Involve a mutual trusted party to help facilitate the conversation', weight: { collaborative: 1, risk_mitigation: 1 } },
      { id: 'd', text: 'Document concerns formally to create appropriate accountability', weight: { formal: 1, process: 1, risk_averse: 1 } }
    ]
  },
  {
    id: 'pro_lead_7',
    phase: 'leadership_influence',
    type: 'reflection',
    question: 'Your natural leadership style is best described as:',
    subtitle: 'Choose the most accurate description',
    options: [
      { id: 'a', text: 'Directive—I provide clear direction and make decisions efficiently', weight: { dominance: 2, directive: 1, control: 1 } },
      { id: 'b', text: 'Collaborative—I build consensus and empower team decision-making', weight: { collaborative: 2, inclusion: 1, empowerment: 1 } },
      { id: 'c', text: 'Coaching—I develop people and help them reach their potential', weight: { coaching: 2, development: 1, nurturing: 1 } },
      { id: 'd', text: 'Visionary—I inspire with compelling future possibilities', weight: { visionary: 2, inspirational: 1, strategic_thinking: 1 } }
    ]
  },
  {
    id: 'pro_lead_8',
    phase: 'leadership_influence',
    type: 'reflection',
    question: 'When delegating important work, your approach is:',
    subtitle: 'Select your typical delegation style',
    options: [
      { id: 'a', text: 'Provide clear objectives and let capable people determine how', weight: { empowerment: 2, trust: 1, delegation: 1 } },
      { id: 'b', text: 'Offer detailed guidance and check in regularly on progress', weight: { structured: 1, oversight: 1, hands_on: 1 } },
      { id: 'c', text: 'Adjust my approach based on the individual\'s experience level', weight: { adaptive: 1, situational: 1, coaching: 1 } },
      { id: 'd', text: 'Retain critical decisions while delegating execution', weight: { control: 1, strategic: 1, selective: 1 } }
    ]
  },
  {
    id: 'pro_lead_9',
    phase: 'leadership_influence',
    type: 'scenario',
    question: 'Your organization is going through significant restructuring. Rumors are creating anxiety among your team.',
    subtitle: 'How do you handle communication?',
    options: [
      { id: 'a', text: 'Share what you know openly, even if information is incomplete', weight: { transparency: 2, trust_building: 1, courage: 1 } },
      { id: 'b', text: 'Communicate only confirmed information to avoid speculation', weight: { cautious: 1, accuracy: 1, measured: 1 } },
      { id: 'c', text: 'Focus the team on their work and what they can control', weight: { practical: 1, focus: 1, pragmatic: 1 } },
      { id: 'd', text: 'Create opportunities for team members to voice concerns', weight: { empathetic: 1, supportive: 1, listening: 1 } }
    ]
  },
  {
    id: 'pro_lead_10',
    phase: 'leadership_influence',
    type: 'forced_choice',
    question: 'When you need to influence a decision but lack formal authority, you:',
    subtitle: 'Choose your typical approach',
    options: [
      { id: 'a', text: 'Build a compelling case with data and logical arguments', weight: { analytical: 1, persuasion: 1, rational: 1 } },
      { id: 'b', text: 'Identify and align with key decision-makers individually', weight: { political_savvy: 1, influence: 1, networking: 1 } },
      { id: 'c', text: 'Create coalition support from stakeholders who share your view', weight: { coalition: 1, collaborative: 1, strategic: 1 } }
    ]
  },
  {
    id: 'pro_lead_11',
    phase: 'leadership_influence',
    type: 'situational_judgement',
    question: 'A talented team member requests a promotion you believe they are not ready for.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would be candid about the gaps while creating a clear development path.',
    weights: {
      strongly_disagree: { avoidant: 1, indirect: 1 },
      disagree: { accommodating: 1, diplomatic: 1 },
      agree: { direct: 1, developmental: 1 },
      strongly_agree: { direct: 2, coaching: 1, integrity: 1 }
    }
  },
  {
    id: 'pro_lead_12',
    phase: 'leadership_influence',
    type: 'scenario',
    question: 'You inherit a team with low morale and a history of poor leadership. Trust is low.',
    subtitle: 'What is your first priority?',
    options: [
      { id: 'a', text: 'Listen extensively to understand the history and current concerns', weight: { empathy: 1, listening: 1, understanding: 1 } },
      { id: 'b', text: 'Establish new expectations and standards clearly', weight: { direction: 1, clarity: 1, structure: 1 } },
      { id: 'c', text: 'Deliver quick wins to demonstrate competent leadership', weight: { results: 1, credibility: 1, action: 1 } },
      { id: 'd', text: 'Build individual relationships with key team members', weight: { relationship: 1, connection: 1, trust_building: 1 } }
    ]
  },
  {
    id: 'pro_lead_13',
    phase: 'leadership_influence',
    type: 'reflection',
    question: 'How do you typically handle disagreement with your supervisor?',
    subtitle: 'Choose your natural response',
    options: [
      { id: 'a', text: 'Express my view clearly, accepting that they have final authority', weight: { assertive: 1, respectful: 1, direct: 1 } },
      { id: 'b', text: 'Align with their direction even if I disagree', weight: { deferential: 1, compliant: 1, hierarchy: 1 } },
      { id: 'c', text: 'Present data and logic to persuade them to reconsider', weight: { influence: 1, analytical: 1, persistent: 1 } },
      { id: 'd', text: 'Seek to understand their perspective before pressing my case', weight: { collaborative: 1, open_minded: 1, empathy: 1 } }
    ]
  },
  {
    id: 'pro_lead_14',
    phase: 'leadership_influence',
    type: 'forced_choice',
    question: 'Your leadership presence is typically characterized by:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Calm confidence that reassures others in uncertain situations', weight: { composure: 2, executive_presence: 1, stability: 1 } },
      { id: 'b', text: 'Energetic enthusiasm that motivates and inspires action', weight: { energy: 2, charisma: 1, inspirational: 1 } },
      { id: 'c', text: 'Thoughtful depth that brings credibility to discussions', weight: { thoughtful: 2, analytical: 1, gravitas: 1 } }
    ]
  },
  {
    id: 'pro_lead_15',
    phase: 'leadership_influence',
    type: 'scenario',
    question: 'You must communicate an unpopular organizational decision to your team that you personally opposed.',
    subtitle: 'How do you handle this situation?',
    options: [
      { id: 'a', text: 'Present it as the organization\'s decision, explaining I advocated differently', weight: { transparent: 1, honest: 1, integrity: 1 } },
      { id: 'b', text: 'Own the decision fully and present the rationale professionally', weight: { loyalty: 1, alignment: 1, professionalism: 1 } },
      { id: 'c', text: 'Focus on helping the team adapt while acknowledging challenges', weight: { supportive: 1, pragmatic: 1, forward_focus: 1 } },
      { id: 'd', text: 'Create space for the team to process while maintaining direction', weight: { empathetic: 1, leadership: 1, balanced: 1 } }
    ]
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 3: EXECUTION DISCIPLINE & PROBLEM SOLVING (35 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  {
    id: 'pro_exec_1',
    phase: 'execution_discipline',
    type: 'situational_judgement',
    question: 'A project is falling behind schedule despite the team working extended hours. The deadline is firm.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would systematically identify bottlenecks and reallocate resources before considering scope changes.',
    weights: {
      strongly_disagree: { reactive: 1, panicked: 1 },
      disagree: { quick_fix: 1, shortcuts: 1 },
      agree: { analytical: 1, problem_solving: 1 },
      strongly_agree: { analytical: 2, execution_discipline: 1, systematic: 1 }
    }
  },
  {
    id: 'pro_exec_2',
    phase: 'execution_discipline',
    type: 'situational_judgement',
    question: 'Multiple priorities compete for your attention. Everything seems urgent.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would stop and explicitly rank priorities based on strategic impact before continuing.',
    weights: {
      strongly_disagree: { reactive: 2, scattered: 1 },
      disagree: { multitasking: 1, responsive: 1 },
      agree: { prioritization: 1, focus: 1 },
      strongly_agree: { strategic_thinking: 1, execution_discipline: 2, prioritization: 1 }
    }
  },
  {
    id: 'pro_exec_3',
    phase: 'execution_discipline',
    type: 'forced_choice',
    question: 'When you take on a new initiative, your first focus is typically:',
    subtitle: 'Select your primary orientation',
    options: [
      { id: 'a', text: 'Understanding the end goal and defining success metrics', weight: { outcome_focus: 2, strategic: 1, clarity: 1 } },
      { id: 'b', text: 'Identifying resources and building the right team', weight: { resourceful: 1, people_focus: 1, preparation: 1 } },
      { id: 'c', text: 'Creating detailed plans and timelines', weight: { planning: 2, structured: 1, organized: 1 } }
    ]
  },
  {
    id: 'pro_exec_4',
    phase: 'execution_discipline',
    type: 'forced_choice',
    question: 'When facing a complex problem, you typically start by:',
    subtitle: 'Choose your natural approach',
    options: [
      { id: 'a', text: 'Breaking it into smaller, manageable components', weight: { analytical: 2, structured_thinking: 1, methodical: 1 } },
      { id: 'b', text: 'Looking for patterns similar to problems you have solved before', weight: { pattern_recognition: 1, experience: 1, intuition: 1 } },
      { id: 'c', text: 'Gathering diverse perspectives on the root cause', weight: { collaborative: 1, comprehensive: 1, inclusion: 1 } }
    ]
  },
  {
    id: 'pro_exec_5',
    phase: 'execution_discipline',
    type: 'scenario',
    question: 'Your team has developed a solution that is technically excellent but will be difficult to implement with current resources.',
    subtitle: 'What is your approach?',
    options: [
      { id: 'a', text: 'Push for the excellent solution and advocate for additional resources', weight: { ambitious: 1, excellence: 1, advocacy: 1 } },
      { id: 'b', text: 'Seek a pragmatic middle ground that balances quality and feasibility', weight: { pragmatic: 1, realistic: 1, balanced: 1 } },
      { id: 'c', text: 'Accept a simpler solution that can be implemented effectively', weight: { practical: 1, execution_focus: 1, humble: 1 } },
      { id: 'd', text: 'Phase the implementation to deliver value incrementally', weight: { strategic: 1, incremental: 1, adaptable: 1 } }
    ]
  },
  {
    id: 'pro_exec_6',
    phase: 'execution_discipline',
    type: 'scenario',
    question: 'You identify a systemic process inefficiency that affects multiple departments beyond your authority.',
    subtitle: 'How do you address this?',
    options: [
      { id: 'a', text: 'Build a compelling case and present it to appropriate leadership', weight: { influence: 1, initiative: 1, analytical: 1 } },
      { id: 'b', text: 'Fix what you can within your sphere and document the broader issue', weight: { pragmatic: 1, ownership: 1, documentation: 1 } },
      { id: 'c', text: 'Form a cross-functional coalition to address the issue collectively', weight: { collaborative: 1, coalition: 1, initiative: 1 } },
      { id: 'd', text: 'Focus on your own area—others are responsible for theirs', weight: { boundaries: 1, focused: 1, limited: 1 } }
    ]
  },
  {
    id: 'pro_exec_7',
    phase: 'execution_discipline',
    type: 'reflection',
    question: 'Your approach to planning is best described as:',
    subtitle: 'Choose the most accurate description',
    options: [
      { id: 'a', text: 'Detailed upfront planning with clear milestones', weight: { planning: 2, structured: 1, methodical: 1 } },
      { id: 'b', text: 'Flexible framework that adapts as circumstances evolve', weight: { adaptable: 1, agile: 1, responsive: 1 } },
      { id: 'c', text: 'Minimal planning—prefer to learn through action', weight: { action_oriented: 1, experimental: 1, spontaneous: 1 } },
      { id: 'd', text: 'Collaborative planning that incorporates diverse input', weight: { inclusive: 1, collaborative: 1, comprehensive: 1 } }
    ]
  },
  {
    id: 'pro_exec_8',
    phase: 'execution_discipline',
    type: 'reflection',
    question: 'When a project encounters unexpected obstacles, your first response is:',
    subtitle: 'Select your typical reaction',
    options: [
      { id: 'a', text: 'Analyze the obstacle systematically to understand root causes', weight: { analytical: 2, problem_solving: 1, thorough: 1 } },
      { id: 'b', text: 'Look for creative alternatives or workarounds', weight: { creative: 1, resourceful: 1, adaptable: 1 } },
      { id: 'c', text: 'Engage the team to brainstorm solutions collectively', weight: { collaborative: 1, inclusive: 1, team_oriented: 1 } },
      { id: 'd', text: 'Assess impact and communicate changes to stakeholders', weight: { stakeholder_mgmt: 1, communication: 1, transparent: 1 } }
    ]
  },
  {
    id: 'pro_exec_9',
    phase: 'execution_discipline',
    type: 'scenario',
    question: 'Your organization has competing strategic initiatives with limited resources. You lead one of them.',
    subtitle: 'How do you ensure your initiative succeeds?',
    options: [
      { id: 'a', text: 'Build a compelling case for resource priority based on strategic value', weight: { influence: 1, advocacy: 1, strategic_thinking: 1 } },
      { id: 'b', text: 'Find creative ways to achieve objectives with current resources', weight: { resourceful: 1, pragmatic: 1, creative: 1 } },
      { id: 'c', text: 'Collaborate with other initiative leaders to find synergies', weight: { collaborative: 1, win_win: 1, creative: 1 } },
      { id: 'd', text: 'Focus on delivering quick wins to demonstrate value and attract resources', weight: { results: 1, credibility: 1, strategic: 1 } }
    ]
  },
  {
    id: 'pro_exec_10',
    phase: 'execution_discipline',
    type: 'forced_choice',
    question: 'In managing execution, you place highest priority on:',
    subtitle: 'Select your primary focus',
    options: [
      { id: 'a', text: 'Maintaining quality standards even if it affects speed', weight: { quality: 2, excellence: 1, standards: 1 } },
      { id: 'b', text: 'Hitting deadlines and maintaining momentum', weight: { execution_intensity: 1, speed: 1, accountability: 1 } },
      { id: 'c', text: 'Balancing multiple factors based on specific context', weight: { balanced: 1, contextual: 1, judgment: 1 } }
    ]
  },
  {
    id: 'pro_exec_11',
    phase: 'execution_discipline',
    type: 'situational_judgement',
    question: 'A team member consistently delivers good work but misses deadlines by small margins.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would address the pattern directly while acknowledging their quality contributions.',
    weights: {
      strongly_disagree: { avoidant: 1, tolerant: 1 },
      disagree: { accommodating: 1, permissive: 1 },
      agree: { direct: 1, balanced: 1 },
      strongly_agree: { accountability: 2, direct: 1, balanced: 1 }
    }
  },
  {
    id: 'pro_exec_12',
    phase: 'execution_discipline',
    type: 'scenario',
    question: 'You realize midway through a project that initial assumptions were wrong. Continuing will produce a suboptimal outcome.',
    subtitle: 'What do you do?',
    options: [
      { id: 'a', text: 'Stop and redesign the approach based on new understanding', weight: { integrity: 1, excellence: 1, courage: 1 } },
      { id: 'b', text: 'Complete the current phase and incorporate learnings in the next', weight: { pragmatic: 1, incremental: 1, momentum: 1 } },
      { id: 'c', text: 'Seek stakeholder input on whether to pivot or continue', weight: { inclusive: 1, collaborative: 1, transparency: 1 } },
      { id: 'd', text: 'Adjust within current constraints to optimize the outcome', weight: { adaptable: 1, pragmatic: 1, resourceful: 1 } }
    ]
  },
  {
    id: 'pro_exec_13',
    phase: 'execution_discipline',
    type: 'reflection',
    question: 'How do you typically maintain focus on long-term goals amid daily operational demands?',
    subtitle: 'Choose your primary method',
    options: [
      { id: 'a', text: 'Scheduled strategic reviews and planning time', weight: { structured: 1, disciplined: 1, planning: 1 } },
      { id: 'b', text: 'Clear delegation of operational matters to capable team members', weight: { delegation: 1, empowerment: 1, strategic_focus: 1 } },
      { id: 'c', text: 'Visual tracking systems that keep priorities visible', weight: { organized: 1, systematic: 1, visual: 1 } },
      { id: 'd', text: 'Regular communication of priorities to keep myself and team aligned', weight: { communication: 1, alignment: 1, transparency: 1 } }
    ]
  },
  {
    id: 'pro_exec_14',
    phase: 'execution_discipline',
    type: 'forced_choice',
    question: 'When taking over a struggling project, your initial approach is:',
    subtitle: 'Select your first priority',
    options: [
      { id: 'a', text: 'Deep diagnostic to understand what went wrong', weight: { analytical: 2, thorough: 1, root_cause: 1 } },
      { id: 'b', text: 'Quick stabilization followed by systematic improvement', weight: { pragmatic: 1, action_oriented: 1, stabilizing: 1 } },
      { id: 'c', text: 'Team conversations to understand perspectives and rebuild morale', weight: { people_focus: 1, empathy: 1, relationship: 1 } }
    ]
  },
  {
    id: 'pro_exec_15',
    phase: 'execution_discipline',
    type: 'scenario',
    question: 'You are asked to deliver results in an area where you lack deep expertise.',
    subtitle: 'How do you approach this challenge?',
    options: [
      { id: 'a', text: 'Invest significant time in learning the domain quickly', weight: { learning_agility: 1, preparation: 1, independent: 1 } },
      { id: 'b', text: 'Surround myself with experts and leverage their knowledge', weight: { resourceful: 1, collaborative: 1, humble: 1 } },
      { id: 'c', text: 'Apply general leadership and problem-solving principles', weight: { transferable: 1, confident: 1, principles: 1 } },
      { id: 'd', text: 'Be transparent about limitations while demonstrating capability', weight: { honest: 1, self_aware: 1, learning: 1 } }
    ]
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 4: ADAPTABILITY & LEARNING AGILITY (35 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  {
    id: 'pro_adapt_1',
    phase: 'adaptability',
    type: 'situational_judgement',
    question: 'Your organization announces a major strategic pivot that requires you to lead in an unfamiliar direction.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would embrace the challenge and view it as an opportunity for growth.',
    weights: {
      strongly_disagree: { change_resistant: 2, fixed: 1 },
      disagree: { cautious: 1, stability: 1 },
      agree: { adaptable: 1, growth_mindset: 1 },
      strongly_agree: { adaptability: 2, learning_agility: 1, resilience: 1 }
    }
  },
  {
    id: 'pro_adapt_2',
    phase: 'adaptability',
    type: 'situational_judgement',
    question: 'A new technology emerges that could transform your industry but requires significant personal learning.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would proactively invest time to understand and potentially leverage this technology.',
    weights: {
      strongly_disagree: { complacent: 2, resistant: 1 },
      disagree: { cautious: 1, skeptical: 1 },
      agree: { curious: 1, proactive: 1 },
      strongly_agree: { learning_agility: 2, innovation: 1, tech_fluency: 1 }
    }
  },
  {
    id: 'pro_adapt_3',
    phase: 'adaptability',
    type: 'forced_choice',
    question: 'When facing significant change, your primary source of confidence is:',
    subtitle: 'Select what grounds you most',
    options: [
      { id: 'a', text: 'Past experience navigating similar transitions successfully', weight: { experience: 1, confidence: 1, pattern_recognition: 1 } },
      { id: 'b', text: 'Belief in my ability to learn and adapt quickly', weight: { learning_agility: 2, self_efficacy: 1, growth_mindset: 1 } },
      { id: 'c', text: 'Support network of capable colleagues and mentors', weight: { network: 1, collaborative: 1, humility: 1 } }
    ]
  },
  {
    id: 'pro_adapt_4',
    phase: 'adaptability',
    type: 'forced_choice',
    question: 'Your reaction to constructive criticism is typically:',
    subtitle: 'Choose your natural response',
    options: [
      { id: 'a', text: 'Appreciate the feedback and seek to understand and improve', weight: { openness: 2, learning_agility: 1, humility: 1 } },
      { id: 'b', text: 'Evaluate the feedback source and context before accepting', weight: { analytical: 1, discerning: 1, selective: 1 } },
      { id: 'c', text: 'Initial defensiveness followed by reflection', weight: { defensive: 1, reflective: 1, evolving: 1 } }
    ]
  },
  {
    id: 'pro_adapt_5',
    phase: 'adaptability',
    type: 'scenario',
    question: 'You are assigned to lead a team in a market and culture very different from your experience.',
    subtitle: 'How do you prepare?',
    options: [
      { id: 'a', text: 'Immerse yourself in learning about the market and cultural context', weight: { learning_agility: 1, preparation: 1, thorough: 1 } },
      { id: 'b', text: 'Rely on local team members and experts to guide your approach', weight: { collaborative: 1, humble: 1, delegation: 1 } },
      { id: 'c', text: 'Apply proven leadership principles while remaining open to adaptation', weight: { confident: 1, principles: 1, adaptable: 1 } },
      { id: 'd', text: 'Spend significant time listening before making any changes', weight: { patient: 1, observant: 1, respectful: 1 } }
    ]
  },
  {
    id: 'pro_adapt_6',
    phase: 'adaptability',
    type: 'scenario',
    question: 'A project you are leading requires you to work closely with teams that have very different working styles.',
    subtitle: 'How do you navigate this?',
    options: [
      { id: 'a', text: 'Adapt my style to work effectively with each team', weight: { adaptability: 2, flexibility: 1, empathy: 1 } },
      { id: 'b', text: 'Establish common processes and expectations for all teams', weight: { structure: 1, standards: 1, consistency: 1 } },
      { id: 'c', text: 'Create translation layers to bridge different working styles', weight: { creative: 1, diplomatic: 1, bridging: 1 } },
      { id: 'd', text: 'Focus on outcomes and let each team work in their preferred way', weight: { outcome_focus: 1, autonomy: 1, flexible: 1 } }
    ]
  },
  {
    id: 'pro_adapt_7',
    phase: 'adaptability',
    type: 'reflection',
    question: 'How do you typically respond when a deeply held assumption proves to be wrong?',
    subtitle: 'Choose your natural response',
    options: [
      { id: 'a', text: 'Update my mental model and apply the learning forward', weight: { learning_agility: 2, adaptability: 1, open_minded: 1 } },
      { id: 'b', text: 'Seek additional evidence before abandoning the assumption', weight: { cautious: 1, analytical: 1, thorough: 1 } },
      { id: 'c', text: 'Acknowledge the error and share the learning with others', weight: { transparent: 1, humble: 1, collaborative: 1 } },
      { id: 'd', text: 'Reflect on how the assumption formed to avoid similar errors', weight: { reflective: 1, analytical: 1, self_aware: 1 } }
    ]
  },
  {
    id: 'pro_adapt_8',
    phase: 'adaptability',
    type: 'reflection',
    question: 'Your approach to continuous learning is best described as:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Proactive and systematic—I schedule learning into my routine', weight: { disciplined: 1, proactive: 1, systematic: 1 } },
      { id: 'b', text: 'Opportunistic—I learn as needs arise and opportunities present', weight: { responsive: 1, practical: 1, contextual: 1 } },
      { id: 'c', text: 'Experiential—I learn primarily through doing and reflecting', weight: { experiential: 1, reflective: 1, practical: 1 } },
      { id: 'd', text: 'Collaborative—I learn best through dialogue and exchange', weight: { collaborative: 1, social: 1, networked: 1 } }
    ]
  },
  {
    id: 'pro_adapt_9',
    phase: 'adaptability',
    type: 'scenario',
    question: 'A technology you have invested years mastering is becoming obsolete due to newer alternatives.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Begin learning the new technology proactively', weight: { learning_agility: 2, proactive: 1, adaptability: 1 } },
      { id: 'b', text: 'Look for roles where my existing expertise remains valuable', weight: { pragmatic: 1, strategic: 1, leveraging: 1 } },
      { id: 'c', text: 'Combine existing expertise with new capabilities to create unique value', weight: { creative: 1, bridging: 1, innovative: 1 } },
      { id: 'd', text: 'Wait to see how the technology landscape stabilizes', weight: { cautious: 1, patient: 1, observant: 1 } }
    ]
  },
  {
    id: 'pro_adapt_10',
    phase: 'adaptability',
    type: 'forced_choice',
    question: 'When entering a new role or environment, you typically:',
    subtitle: 'Choose your natural approach',
    options: [
      { id: 'a', text: 'Spend significant time observing before taking action', weight: { patient: 1, observant: 1, cautious: 1 } },
      { id: 'b', text: 'Take early action to establish presence and test hypotheses', weight: { action_oriented: 1, bold: 1, experimental: 1 } },
      { id: 'c', text: 'Build relationships and gather information simultaneously', weight: { balanced: 1, relationship: 1, comprehensive: 1 } }
    ]
  },
  {
    id: 'pro_adapt_11',
    phase: 'adaptability',
    type: 'situational_judgement',
    question: 'Your organization adopts a new system that changes how you work significantly.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would actively seek to master the new system quickly rather than relying on workarounds.',
    weights: {
      strongly_disagree: { resistant: 2, fixed: 1 },
      disagree: { skeptical: 1, cautious: 1 },
      agree: { adaptable: 1, compliant: 1 },
      strongly_agree: { learning_agility: 2, adaptability: 1, proactive: 1 }
    }
  },
  {
    id: 'pro_adapt_12',
    phase: 'adaptability',
    type: 'scenario',
    question: 'You receive feedback that your leadership style is not resonating with a new team you are leading.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Actively adapt your approach to better connect with the team', weight: { adaptability: 2, responsive: 1, empathy: 1 } },
      { id: 'b', text: 'Maintain your style while giving the team time to adjust', weight: { conviction: 1, patience: 1, consistency: 1 } },
      { id: 'c', text: 'Have open conversations to understand what would work better', weight: { collaborative: 1, open_minded: 1, curious: 1 } },
      { id: 'd', text: 'Analyze what specifically is not working and make targeted adjustments', weight: { analytical: 1, selective: 1, thoughtful: 1 } }
    ]
  },
  {
    id: 'pro_adapt_13',
    phase: 'adaptability',
    type: 'reflection',
    question: 'When facing ambiguity and uncertainty, you typically:',
    subtitle: 'Choose your natural response',
    options: [
      { id: 'a', text: 'Feel energized by the challenge and possibilities', weight: { ambiguity_tolerance: 2, adventurous: 1, confident: 1 } },
      { id: 'b', text: 'Work to create clarity and structure quickly', weight: { structured: 1, organizing: 1, comfort_seeking: 1 } },
      { id: 'c', text: 'Accept it as part of leadership and manage discomfort', weight: { resilience: 1, pragmatic: 1, mature: 1 } },
      { id: 'd', text: 'Seek additional information to reduce uncertainty', weight: { analytical: 1, cautious: 1, thorough: 1 } }
    ]
  },
  {
    id: 'pro_adapt_14',
    phase: 'adaptability',
    type: 'forced_choice',
    question: 'Your strongest asset in navigating change is:',
    subtitle: 'Select your primary strength',
    options: [
      { id: 'a', text: 'Ability to quickly learn and apply new knowledge', weight: { learning_agility: 2, quick: 1, application: 1 } },
      { id: 'b', text: 'Emotional resilience and composure under pressure', weight: { resilience: 2, composure: 1, stability: 1 } },
      { id: 'c', text: 'Relationships and network that provide support and resources', weight: { network: 1, collaborative: 1, resources: 1 } }
    ]
  },
  {
    id: 'pro_adapt_15',
    phase: 'adaptability',
    type: 'scenario',
    question: 'A mentor advises you to develop a capability that you do not see as a personal strength.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Take the advice seriously and commit to development in that area', weight: { coachable: 1, growth_mindset: 1, humility: 1 } },
      { id: 'b', text: 'Evaluate whether that capability is truly essential for your goals', weight: { strategic: 1, selective: 1, self_directed: 1 } },
      { id: 'c', text: 'Focus on leveraging strengths while managing around the weakness', weight: { strengths_based: 1, pragmatic: 1, self_aware: 1 } },
      { id: 'd', text: 'Seek additional perspectives before deciding on development focus', weight: { thoughtful: 1, comprehensive: 1, deliberate: 1 } }
    ]
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 5: INTEGRITY & EMOTIONAL INTELLIGENCE (35 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  {
    id: 'pro_integ_1',
    phase: 'integrity_eq',
    type: 'situational_judgement',
    question: 'You discover that a successful outcome was achieved through shortcuts that violated organizational values.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would address the violation even though the outcome was positive.',
    weights: {
      strongly_disagree: { ends_justify_means: 2, pragmatic: 1 },
      disagree: { outcome_focused: 1, tolerant: 1 },
      agree: { integrity: 1, values_driven: 1 },
      strongly_agree: { integrity: 2, values_driven: 1, accountability: 1 }
    }
  },
  {
    id: 'pro_integ_2',
    phase: 'integrity_eq',
    type: 'situational_judgement',
    question: 'A colleague shares confidential information that could benefit your project significantly.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would decline to use the information due to how it was obtained.',
    weights: {
      strongly_disagree: { opportunistic: 2, ends_justify: 1 },
      disagree: { pragmatic: 1, utilitarian: 1 },
      agree: { ethical: 1, cautious: 1 },
      strongly_agree: { integrity: 2, ethical: 1, principled: 1 }
    }
  },
  {
    id: 'pro_integ_3',
    phase: 'integrity_eq',
    type: 'forced_choice',
    question: 'When facing pressure to compromise on principles, you typically:',
    subtitle: 'Choose your natural response',
    options: [
      { id: 'a', text: 'Hold firm on principles regardless of consequences', weight: { integrity: 2, principled: 1, conviction: 1 } },
      { id: 'b', text: 'Seek creative solutions that honor principles and address pressures', weight: { creative: 1, diplomatic: 1, balanced: 1 } },
      { id: 'c', text: 'Evaluate the specific situation—principles have context', weight: { contextual: 1, flexible: 1, pragmatic: 1 } }
    ]
  },
  {
    id: 'pro_integ_4',
    phase: 'integrity_eq',
    type: 'forced_choice',
    question: 'Your approach to managing your emotions in high-stakes situations is:',
    subtitle: 'Select your primary method',
    options: [
      { id: 'a', text: 'Remain calm by focusing on facts and logical analysis', weight: { composure: 1, analytical: 1, rational: 1 } },
      { id: 'b', text: 'Acknowledge emotions privately while maintaining professional demeanor', weight: { self_aware: 1, emotional_regulation: 1, mature: 1 } },
      { id: 'c', text: 'Channel emotional energy into focused action and determination', weight: { energizing: 1, driven: 1, passionate: 1 } }
    ]
  },
  {
    id: 'pro_integ_5',
    phase: 'integrity_eq',
    type: 'scenario',
    question: 'You realize you have unintentionally misled stakeholders due to incorrect information you provided.',
    subtitle: 'How do you handle this?',
    options: [
      { id: 'a', text: 'Immediately communicate the correction and take accountability', weight: { integrity: 2, transparency: 1, accountability: 1 } },
      { id: 'b', text: 'Assess the impact first and then determine appropriate correction', weight: { analytical: 1, measured: 1, strategic: 1 } },
      { id: 'c', text: 'Correct going forward without drawing attention to the error', weight: { pragmatic: 1, discreet: 1, minimizing: 1 } },
      { id: 'd', text: 'Work with affected stakeholders individually to address the error', weight: { relationship: 1, personal: 1, careful: 1 } }
    ]
  },
  {
    id: 'pro_integ_6',
    phase: 'integrity_eq',
    type: 'scenario',
    question: 'A team member is going through a significant personal crisis that is affecting their work performance.',
    subtitle: 'How do you approach this situation?',
    options: [
      { id: 'a', text: 'Have a caring conversation and offer flexibility while the crisis passes', weight: { empathy: 2, supportive: 1, compassionate: 1 } },
      { id: 'b', text: 'Maintain performance expectations while being understanding', weight: { balanced: 1, fair: 1, standards: 1 } },
      { id: 'c', text: 'Connect them with appropriate support resources', weight: { helpful: 1, boundaries: 1, resourceful: 1 } },
      { id: 'd', text: 'Redistribute their work temporarily to reduce pressure', weight: { practical: 1, protective: 1, team_focused: 1 } }
    ]
  },
  {
    id: 'pro_integ_7',
    phase: 'integrity_eq',
    type: 'reflection',
    question: 'How well do you typically read the emotional dynamics in a room or meeting?',
    subtitle: 'Choose the most accurate description',
    options: [
      { id: 'a', text: 'Very well—I pick up on subtle cues and undercurrents quickly', weight: { emotional_intelligence: 2, perceptive: 1, aware: 1 } },
      { id: 'b', text: 'Reasonably well when I consciously focus on it', weight: { capable: 1, intentional: 1, developing: 1 } },
      { id: 'c', text: 'I tend to focus more on content than emotional dynamics', weight: { task_focused: 1, analytical: 1, less_aware: 1 } },
      { id: 'd', text: 'I rely on others to surface emotional information', weight: { collaborative: 1, delegating: 1, limited: 1 } }
    ]
  },
  {
    id: 'pro_integ_8',
    phase: 'integrity_eq',
    type: 'reflection',
    question: 'When you make a mistake that affects others, your typical response is:',
    subtitle: 'Select your natural approach',
    options: [
      { id: 'a', text: 'Acknowledge it openly, apologize, and focus on making it right', weight: { accountable: 2, transparent: 1, action_oriented: 1 } },
      { id: 'b', text: 'Fix the issue quickly while minimizing attention to the error', weight: { pragmatic: 1, discreet: 1, solution_focused: 1 } },
      { id: 'c', text: 'Analyze what led to the mistake to prevent recurrence', weight: { analytical: 1, learning: 1, preventive: 1 } },
      { id: 'd', text: 'Discuss with those affected to understand and address impact', weight: { empathetic: 1, relational: 1, collaborative: 1 } }
    ]
  },
  {
    id: 'pro_integ_9',
    phase: 'integrity_eq',
    type: 'scenario',
    question: 'You witness a senior leader behaving in ways that violate organizational values but no formal rules.',
    subtitle: 'What do you do?',
    options: [
      { id: 'a', text: 'Raise the concern through appropriate channels', weight: { integrity: 1, courage: 1, principled: 1 } },
      { id: 'b', text: 'Address it directly with the leader if you have that relationship', weight: { direct: 1, courageous: 1, relationship: 1 } },
      { id: 'c', text: 'Focus on your own sphere and set a positive example', weight: { pragmatic: 1, influence: 1, example: 1 } },
      { id: 'd', text: 'Document observations in case the behavior escalates', weight: { cautious: 1, protective: 1, evidence: 1 } }
    ]
  },
  {
    id: 'pro_integ_10',
    phase: 'integrity_eq',
    type: 'forced_choice',
    question: 'When you feel frustrated with a colleague\'s approach, you typically:',
    subtitle: 'Choose your natural response',
    options: [
      { id: 'a', text: 'Express your perspective directly and constructively', weight: { direct: 1, assertive: 1, transparent: 1 } },
      { id: 'b', text: 'Process privately before deciding whether to raise it', weight: { reflective: 1, measured: 1, self_regulated: 1 } },
      { id: 'c', text: 'Seek to understand their perspective before judging', weight: { empathetic: 1, open_minded: 1, curious: 1 } }
    ]
  },
  {
    id: 'pro_integ_11',
    phase: 'integrity_eq',
    type: 'situational_judgement',
    question: 'You are asked to present results that, while technically accurate, could be interpreted misleadingly.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would add context to ensure accurate interpretation even if it weakens the narrative.',
    weights: {
      strongly_disagree: { manipulative: 2, expedient: 1 },
      disagree: { pragmatic: 1, selective: 1 },
      agree: { honest: 1, fair: 1 },
      strongly_agree: { integrity: 2, transparent: 1, principled: 1 }
    }
  },
  {
    id: 'pro_integ_12',
    phase: 'integrity_eq',
    type: 'scenario',
    question: 'A team member confides that they are struggling with the workload but asks you not to tell anyone.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Respect their confidence while helping them find solutions', weight: { trust: 1, supportive: 1, collaborative: 1 } },
      { id: 'b', text: 'Encourage them to raise the issue themselves appropriately', weight: { empowering: 1, coaching: 1, boundaries: 1 } },
      { id: 'c', text: 'Assess whether the situation requires you to intervene despite their request', weight: { judgment: 1, responsible: 1, protective: 1 } },
      { id: 'd', text: 'Take action to redistribute work without revealing their confidence', weight: { discreet: 1, protective: 1, pragmatic: 1 } }
    ]
  },
  {
    id: 'pro_integ_13',
    phase: 'integrity_eq',
    type: 'reflection',
    question: 'How do you typically build trust with new team members or colleagues?',
    subtitle: 'Choose your primary approach',
    options: [
      { id: 'a', text: 'Consistent follow-through on commitments over time', weight: { reliable: 2, consistent: 1, trustworthy: 1 } },
      { id: 'b', text: 'Open and honest communication from the start', weight: { transparent: 1, authentic: 1, direct: 1 } },
      { id: 'c', text: 'Demonstrating genuine interest in their success', weight: { supportive: 1, caring: 1, empathetic: 1 } },
      { id: 'd', text: 'Sharing appropriate vulnerability and admitting limitations', weight: { authentic: 1, humble: 1, human: 1 } }
    ]
  },
  {
    id: 'pro_integ_14',
    phase: 'integrity_eq',
    type: 'forced_choice',
    question: 'When you need to deliver difficult news, your approach is:',
    subtitle: 'Select your primary style',
    options: [
      { id: 'a', text: 'Direct and clear—people deserve honesty', weight: { direct: 2, honest: 1, clear: 1 } },
      { id: 'b', text: 'Thoughtfully framed to minimize unnecessary pain', weight: { compassionate: 1, thoughtful: 1, diplomatic: 1 } },
      { id: 'c', text: 'Balanced between directness and sensitivity to context', weight: { balanced: 1, situational: 1, adaptive: 1 } }
    ]
  },
  {
    id: 'pro_integ_15',
    phase: 'integrity_eq',
    type: 'scenario',
    question: 'You notice that organizational incentives are driving behavior that conflicts with stated values.',
    subtitle: 'What do you do?',
    options: [
      { id: 'a', text: 'Raise the misalignment with leadership and propose solutions', weight: { integrity: 1, initiative: 1, courageous: 1 } },
      { id: 'b', text: 'Model values-aligned behavior within your sphere of influence', weight: { example: 1, influence: 1, pragmatic: 1 } },
      { id: 'c', text: 'Document the disconnect and its impacts for future reference', weight: { analytical: 1, cautious: 1, evidence: 1 } },
      { id: 'd', text: 'Adapt to the system while maintaining personal principles', weight: { pragmatic: 1, adaptive: 1, personal: 1 } }
    ]
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 6: INNOVATION & ENTREPRENEURIAL MINDSET (25 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  {
    id: 'pro_innov_1',
    phase: 'innovation',
    type: 'situational_judgement',
    question: 'Your team proposes an unconventional approach that could significantly improve results but carries execution risk.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would support testing the approach with appropriate risk mitigation.',
    weights: {
      strongly_disagree: { risk_averse: 2, conservative: 1 },
      disagree: { cautious: 1, traditional: 1 },
      agree: { innovation: 1, open: 1 },
      strongly_agree: { innovation: 2, entrepreneurial: 1, measured_risk: 1 }
    }
  },
  {
    id: 'pro_innov_2',
    phase: 'innovation',
    type: 'forced_choice',
    question: 'When you see an opportunity for innovation, you typically:',
    subtitle: 'Choose your natural approach',
    options: [
      { id: 'a', text: 'Act quickly to capture the opportunity before others', weight: { entrepreneurial: 2, decisive: 1, action_bias: 1 } },
      { id: 'b', text: 'Build a careful business case before investing resources', weight: { analytical: 1, prudent: 1, methodical: 1 } },
      { id: 'c', text: 'Test with small experiments to validate assumptions', weight: { experimental: 1, measured_risk: 1, learning: 1 } }
    ]
  },
  {
    id: 'pro_innov_3',
    phase: 'innovation',
    type: 'scenario',
    question: 'A promising new venture within your organization is struggling after initial success.',
    subtitle: 'How do you approach the situation?',
    options: [
      { id: 'a', text: 'Analyze what changed and identify adjustments needed', weight: { analytical: 1, problem_solving: 1, systematic: 1 } },
      { id: 'b', text: 'Bring in fresh perspectives to challenge current assumptions', weight: { open_minded: 1, innovative: 1, collaborative: 1 } },
      { id: 'c', text: 'Cut losses quickly if fundamentals have changed', weight: { pragmatic: 1, decisive: 1, risk_aware: 1 } },
      { id: 'd', text: 'Double down with conviction—ventures often have difficult phases', weight: { persistent: 1, conviction: 1, resilience: 1 } }
    ]
  },
  {
    id: 'pro_innov_4',
    phase: 'innovation',
    type: 'reflection',
    question: 'Your orientation toward established processes and methods is:',
    subtitle: 'Choose the most accurate description',
    options: [
      { id: 'a', text: 'Challenge them constantly—innovation requires questioning everything', weight: { innovative: 2, challenger: 1, disruptive: 1 } },
      { id: 'b', text: 'Respect them while looking for improvement opportunities', weight: { balanced: 1, respectful: 1, improving: 1 } },
      { id: 'c', text: 'Follow them unless clear evidence shows a better way', weight: { evidence_based: 1, conservative: 1, careful: 1 } },
      { id: 'd', text: 'Depend on context—some areas need stability, others innovation', weight: { contextual: 1, selective: 1, judgment: 1 } }
    ]
  },
  {
    id: 'pro_innov_5',
    phase: 'innovation',
    type: 'forced_choice',
    question: 'When leading innovation efforts, your biggest challenge is typically:',
    subtitle: 'Select your primary struggle',
    options: [
      { id: 'a', text: 'Generating breakthrough ideas—I excel at execution', weight: { executor: 1, implementer: 1, operational: 1 } },
      { id: 'b', text: 'Gaining organizational support—I have many ideas', weight: { idea_rich: 1, influence: 1, political: 1 } },
      { id: 'c', text: 'Translating ideas into practical implementation', weight: { practical: 1, bridging: 1, operational: 1 } }
    ]
  },
  {
    id: 'pro_innov_6',
    phase: 'innovation',
    type: 'scenario',
    question: 'You have an innovative idea that would require significant organizational change to implement.',
    subtitle: 'How do you proceed?',
    options: [
      { id: 'a', text: 'Build coalition support before formally proposing the change', weight: { political_savvy: 1, strategic: 1, coalition: 1 } },
      { id: 'b', text: 'Develop a detailed business case and present to decision-makers', weight: { analytical: 1, formal: 1, thorough: 1 } },
      { id: 'c', text: 'Pilot the idea in a limited scope to prove value', weight: { experimental: 1, evidence: 1, pragmatic: 1 } },
      { id: 'd', text: 'Champion the idea passionately to inspire others', weight: { visionary: 1, inspirational: 1, passionate: 1 } }
    ]
  },
  {
    id: 'pro_innov_7',
    phase: 'innovation',
    type: 'reflection',
    question: 'How do you typically generate new ideas and solutions?',
    subtitle: 'Choose your primary method',
    options: [
      { id: 'a', text: 'Exposure to diverse inputs—reading, conversations, experiences', weight: { curious: 1, networked: 1, eclectic: 1 } },
      { id: 'b', text: 'Deep analysis of problems until new approaches emerge', weight: { analytical: 1, deep_thinking: 1, systematic: 1 } },
      { id: 'c', text: 'Brainstorming and collaboration with others', weight: { collaborative: 1, social: 1, generative: 1 } },
      { id: 'd', text: 'Intuitive insights that emerge from experience', weight: { intuitive: 1, experiential: 1, pattern_based: 1 } }
    ]
  },
  {
    id: 'pro_innov_8',
    phase: 'innovation',
    type: 'situational_judgement',
    question: 'A routine process is working adequately but shows potential for significant improvement with investment.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would advocate for the improvement even though current performance is acceptable.',
    weights: {
      strongly_disagree: { complacent: 2, status_quo: 1 },
      disagree: { pragmatic: 1, conservative: 1 },
      agree: { improvement: 1, proactive: 1 },
      strongly_agree: { continuous_improvement: 2, innovation: 1, excellence: 1 }
    }
  },
  {
    id: 'pro_innov_9',
    phase: 'innovation',
    type: 'scenario',
    question: 'An experiment you championed has clearly failed. Resources have been spent.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Extract learnings, share them openly, and apply to future efforts', weight: { learning: 1, transparent: 1, forward: 1 } },
      { id: 'b', text: 'Analyze what could have been done differently to avoid the outcome', weight: { analytical: 1, reflective: 1, preventive: 1 } },
      { id: 'c', text: 'Accept it as part of innovation and move to the next opportunity', weight: { resilient: 1, forward: 1, risk_tolerant: 1 } },
      { id: 'd', text: 'Take accountability and ensure stakeholders understand the rationale', weight: { accountable: 1, transparent: 1, responsible: 1 } }
    ]
  },
  {
    id: 'pro_innov_10',
    phase: 'innovation',
    type: 'forced_choice',
    question: 'Your tolerance for uncertainty in pursuing new opportunities is:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'High—uncertainty is where opportunity lives', weight: { risk_tolerance: 2, entrepreneurial: 1, bold: 1 } },
      { id: 'b', text: 'Moderate—I prefer calculated risks with clear rationale', weight: { measured: 1, analytical: 1, balanced: 1 } },
      { id: 'c', text: 'Low—I prefer clearer paths with higher probability of success', weight: { conservative: 1, risk_averse: 1, cautious: 1 } }
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // EXTENDED QUESTION BANK - STRATEGIC DEPTH (Additional 85 questions)
  // ═══════════════════════════════════════════════════════════════════════════════

  // STRATEGIC THINKING EXTENSION
  {
    id: 'pro_strat_ext_1',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'Your organization is presented with an acquisition opportunity that could double market share but requires significant debt.',
    subtitle: 'How do you approach the analysis?',
    options: [
      { id: 'a', text: 'Model multiple scenarios including worst-case debt servicing', weight: { analytical: 2, risk_aware: 1, thorough: 1 } },
      { id: 'b', text: 'Focus on strategic fit and long-term market positioning', weight: { strategic: 2, visionary: 1, growth: 1 } },
      { id: 'c', text: 'Assess integration complexity and cultural alignment first', weight: { operational: 1, cultural: 1, practical: 1 } },
      { id: 'd', text: 'Benchmark against competitor responses to similar opportunities', weight: { competitive: 1, benchmarking: 1, market_aware: 1 } }
    ]
  },
  {
    id: 'pro_strat_ext_2',
    phase: 'strategic_decision',
    type: 'forced_choice',
    question: 'When evaluating strategic options, which dimension carries the most weight in your analysis?',
    subtitle: 'Select your primary consideration',
    options: [
      { id: 'a', text: 'Financial return and value creation potential', weight: { financial: 2, value_focused: 1, quantitative: 1 } },
      { id: 'b', text: 'Competitive advantage and market differentiation', weight: { competitive: 2, strategic: 1, positioning: 1 } },
      { id: 'c', text: 'Organizational capability and execution feasibility', weight: { operational: 2, realistic: 1, pragmatic: 1 } },
      { id: 'd', text: 'Stakeholder impact and alignment with values', weight: { stakeholder: 1, values: 1, ethical: 1 } }
    ]
  },
  {
    id: 'pro_strat_ext_3',
    phase: 'strategic_decision',
    type: 'situational_judgement',
    question: 'A strategic initiative you championed is showing early signs of underperformance against projections.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would proactively raise concerns and propose course corrections before being asked.',
    weights: {
      strongly_disagree: { avoidant: 2, defensive: 1 },
      disagree: { cautious: 1, timing: 1 },
      agree: { proactive: 1, accountable: 1 },
      strongly_agree: { transparent: 2, accountable: 2, courageous: 1 }
    }
  },
  {
    id: 'pro_strat_ext_4',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'Market conditions have changed dramatically, invalidating your current three-year strategy.',
    subtitle: 'What is your immediate response?',
    options: [
      { id: 'a', text: 'Convene a rapid strategic review with key stakeholders', weight: { collaborative: 1, decisive: 1, responsive: 1 } },
      { id: 'b', text: 'Analyze the new conditions deeply before communicating changes', weight: { analytical: 1, thorough: 1, measured: 1 } },
      { id: 'c', text: 'Identify quick wins that align with new conditions', weight: { pragmatic: 1, adaptive: 1, opportunistic: 1 } },
      { id: 'd', text: 'Communicate transparently about uncertainty while developing new direction', weight: { transparent: 1, leadership: 1, honest: 1 } }
    ]
  },
  {
    id: 'pro_strat_ext_5',
    phase: 'strategic_decision',
    type: 'reflection',
    question: 'How far into the future does your typical strategic thinking extend?',
    subtitle: 'Select the timeframe that feels most natural',
    options: [
      { id: 'a', text: '1-2 years - focused on near-term execution and results', weight: { tactical: 1, execution: 1, short_term: 1 } },
      { id: 'b', text: '3-5 years - balanced between execution and positioning', weight: { balanced: 1, medium_term: 1, strategic: 1 } },
      { id: 'c', text: '5-10 years - emphasis on long-term positioning and trends', weight: { visionary: 1, long_term: 2, strategic: 1 } },
      { id: 'd', text: 'Variable - depends on the nature of the decision', weight: { adaptive: 1, contextual: 1, flexible: 1 } }
    ]
  },
  {
    id: 'pro_strat_ext_6',
    phase: 'strategic_decision',
    type: 'forced_choice',
    question: 'When facing a complex strategic decision with incomplete information, you typically:',
    subtitle: 'Choose your natural approach',
    options: [
      { id: 'a', text: 'Gather more data until comfortable with the decision', weight: { thorough: 1, analytical: 1, risk_averse: 1 } },
      { id: 'b', text: 'Make a directional call and adjust as information emerges', weight: { decisive: 2, adaptive: 1, action_bias: 1 } },
      { id: 'c', text: 'Seek diverse perspectives to fill information gaps', weight: { collaborative: 1, inclusive: 1, wisdom_seeking: 1 } },
      { id: 'd', text: 'Trust pattern recognition from similar past situations', weight: { intuitive: 2, experiential: 1, confident: 1 } }
    ]
  },
  {
    id: 'pro_strat_ext_7',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'You discover that a competitor is about to launch a product that directly threatens your core business.',
    subtitle: 'What is your primary focus?',
    options: [
      { id: 'a', text: 'Accelerate your own innovation to respond competitively', weight: { competitive: 1, proactive: 1, innovation: 1 } },
      { id: 'b', text: 'Strengthen relationships with key customers to ensure loyalty', weight: { relationship: 1, defensive: 1, customer: 1 } },
      { id: 'c', text: 'Analyze competitor weaknesses for counter-positioning', weight: { analytical: 1, strategic: 1, competitive: 1 } },
      { id: 'd', text: 'Consider diversification to reduce single-product dependency', weight: { strategic: 1, risk_mgmt: 1, portfolio: 1 } }
    ]
  },
  {
    id: 'pro_strat_ext_8',
    phase: 'strategic_decision',
    type: 'situational_judgement',
    question: 'Senior leadership is pursuing a strategy you believe is fundamentally flawed.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would respectfully but clearly voice my dissent, even if it creates friction.',
    weights: {
      strongly_disagree: { conforming: 2, conflict_averse: 1 },
      disagree: { diplomatic: 1, cautious: 1 },
      agree: { principled: 1, voice: 1 },
      strongly_agree: { courageous: 2, principled: 2, integrity: 1 }
    }
  },
  {
    id: 'pro_strat_ext_9',
    phase: 'strategic_decision',
    type: 'forced_choice',
    question: 'Your approach to resource allocation across competing priorities is best described as:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Concentrated bets on highest-potential opportunities', weight: { bold: 2, focused: 1, conviction: 1 } },
      { id: 'b', text: 'Balanced distribution with portfolio thinking', weight: { balanced: 1, diversified: 1, risk_managed: 1 } },
      { id: 'c', text: 'Flexible allocation that shifts with emerging data', weight: { adaptive: 1, responsive: 1, dynamic: 1 } },
      { id: 'd', text: 'Staged investment with clear milestone gates', weight: { disciplined: 1, structured: 1, rigorous: 1 } }
    ]
  },
  {
    id: 'pro_strat_ext_10',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'A new technology could disrupt your industry within 3-5 years but requires significant investment to pursue.',
    subtitle: 'How do you approach this decision?',
    options: [
      { id: 'a', text: 'Invest early to be a leader if the technology succeeds', weight: { first_mover: 2, bold: 1, visionary: 1 } },
      { id: 'b', text: 'Wait for more validation before committing resources', weight: { cautious: 1, prudent: 1, wait_see: 1 } },
      { id: 'c', text: 'Make small exploratory investments to build learning', weight: { exploratory: 1, learning: 1, hedged: 1 } },
      { id: 'd', text: 'Partner with others to share risk while maintaining optionality', weight: { collaborative: 1, risk_sharing: 1, strategic: 1 } }
    ]
  },

  // LEADERSHIP INFLUENCE EXTENSION
  {
    id: 'pro_lead_ext_1',
    phase: 'leadership_influence',
    type: 'scenario',
    question: 'A high-performing team member has been offered a role at a competitor. They are conflicted about leaving.',
    subtitle: 'How do you handle this conversation?',
    options: [
      { id: 'a', text: 'Discuss their career aspirations and explore internal opportunities', weight: { developmental: 1, caring: 1, retention: 1 } },
      { id: 'b', text: 'Be direct about the value they bring and what you can offer', weight: { direct: 1, transparent: 1, persuasive: 1 } },
      { id: 'c', text: 'Support their decision even if it means losing them', weight: { supportive: 2, selfless: 1, integrity: 1 } },
      { id: 'd', text: 'Understand what the competitor offers and counter strategically', weight: { competitive: 1, negotiating: 1, retention: 1 } }
    ]
  },
  {
    id: 'pro_lead_ext_2',
    phase: 'leadership_influence',
    type: 'forced_choice',
    question: 'When building a new team, which quality do you prioritize most?',
    subtitle: 'Select your top priority',
    options: [
      { id: 'a', text: 'Complementary skills that fill capability gaps', weight: { strategic: 1, capability: 1, balanced: 1 } },
      { id: 'b', text: 'Cultural fit and values alignment', weight: { culture: 2, values: 1, cohesion: 1 } },
      { id: 'c', text: 'Diverse perspectives and cognitive styles', weight: { diversity: 2, innovation: 1, inclusive: 1 } },
      { id: 'd', text: 'Proven track record and demonstrated results', weight: { performance: 1, experience: 1, reliable: 1 } }
    ]
  },
  {
    id: 'pro_lead_ext_3',
    phase: 'leadership_influence',
    type: 'situational_judgement',
    question: 'Your team is facing a significant setback that was partially caused by your own misjudgment.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would openly acknowledge my role in the setback before discussing next steps.',
    weights: {
      strongly_disagree: { deflecting: 2, defensive: 1 },
      disagree: { protective: 1, cautious: 1 },
      agree: { accountable: 1, transparent: 1 },
      strongly_agree: { accountable: 2, courageous: 1, modeling: 1 }
    }
  },
  {
    id: 'pro_lead_ext_4',
    phase: 'leadership_influence',
    type: 'scenario',
    question: 'Two senior leaders on your team are in persistent conflict that is affecting team performance.',
    subtitle: 'How do you address this?',
    options: [
      { id: 'a', text: 'Facilitate a direct conversation to address underlying issues', weight: { facilitative: 1, direct: 1, conflict_mgmt: 1 } },
      { id: 'b', text: 'Coach each individually before bringing them together', weight: { coaching: 1, empathetic: 1, methodical: 1 } },
      { id: 'c', text: 'Set clear expectations about collaboration and hold both accountable', weight: { directive: 1, standards: 1, accountable: 1 } },
      { id: 'd', text: 'Restructure responsibilities to minimize interaction points', weight: { pragmatic: 1, structural: 1, avoidance: 1 } }
    ]
  },
  {
    id: 'pro_lead_ext_5',
    phase: 'leadership_influence',
    type: 'reflection',
    question: 'How do you typically communicate difficult organizational news to your team?',
    subtitle: 'Choose your primary approach',
    options: [
      { id: 'a', text: 'Direct and factual with clear implications', weight: { direct: 2, honest: 1, clear: 1 } },
      { id: 'b', text: 'Empathetic framing with focus on support available', weight: { empathetic: 2, supportive: 1, caring: 1 } },
      { id: 'c', text: 'Balanced between reality and optimistic path forward', weight: { balanced: 1, hopeful: 1, realistic: 1 } },
      { id: 'd', text: 'Interactive with space for questions and concerns', weight: { participative: 1, listening: 1, inclusive: 1 } }
    ]
  },
  {
    id: 'pro_lead_ext_6',
    phase: 'leadership_influence',
    type: 'forced_choice',
    question: 'When delegating critical work, you most naturally focus on:',
    subtitle: 'Select your primary emphasis',
    options: [
      { id: 'a', text: 'Clear outcomes with freedom on approach', weight: { empowering: 2, outcome: 1, trusting: 1 } },
      { id: 'b', text: 'Detailed guidance with regular check-ins', weight: { structured: 1, controlling: 1, monitoring: 1 } },
      { id: 'c', text: 'Development opportunity with coaching support', weight: { developmental: 2, coaching: 1, growth: 1 } },
      { id: 'd', text: 'Resource access and barrier removal', weight: { enabling: 1, supportive: 1, servant: 1 } }
    ]
  },
  {
    id: 'pro_lead_ext_7',
    phase: 'leadership_influence',
    type: 'scenario',
    question: 'You need to implement an unpopular but necessary organizational change.',
    subtitle: 'How do you approach building support?',
    options: [
      { id: 'a', text: 'Start with key influencers to build early coalition', weight: { political: 1, strategic: 1, coalition: 1 } },
      { id: 'b', text: 'Communicate compelling rationale to everyone simultaneously', weight: { transparent: 1, consistent: 1, inclusive: 1 } },
      { id: 'c', text: 'Involve affected parties in shaping implementation details', weight: { participative: 2, empowering: 1, ownership: 1 } },
      { id: 'd', text: 'Lead decisively and manage resistance as it emerges', weight: { directive: 1, decisive: 1, authoritative: 1 } }
    ]
  },
  {
    id: 'pro_lead_ext_8',
    phase: 'leadership_influence',
    type: 'situational_judgement',
    question: 'A junior team member has an innovative idea that challenges your approach.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would genuinely explore their idea even if it means reconsidering my own approach.',
    weights: {
      strongly_disagree: { closed: 2, hierarchical: 1 },
      disagree: { cautious: 1, skeptical: 1 },
      agree: { open: 1, learning: 1 },
      strongly_agree: { humble: 2, inclusive: 1, growth_mindset: 1 }
    }
  },
  {
    id: 'pro_lead_ext_9',
    phase: 'leadership_influence',
    type: 'forced_choice',
    question: 'Your natural leadership presence is best described as:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Commanding - people look to you for direction', weight: { authoritative: 2, presence: 1, directing: 1 } },
      { id: 'b', text: 'Inspiring - people feel energized and motivated', weight: { charismatic: 2, energizing: 1, motivating: 1 } },
      { id: 'c', text: 'Supportive - people feel safe and valued', weight: { nurturing: 2, safe: 1, caring: 1 } },
      { id: 'd', text: 'Collaborative - people feel like partners', weight: { egalitarian: 1, partnering: 1, inclusive: 1 } }
    ]
  },
  {
    id: 'pro_lead_ext_10',
    phase: 'leadership_influence',
    type: 'scenario',
    question: 'You are leading a cross-functional initiative where you have influence but not direct authority.',
    subtitle: 'How do you drive progress?',
    options: [
      { id: 'a', text: 'Build personal relationships with key stakeholders', weight: { relationship: 2, influence: 1, networking: 1 } },
      { id: 'b', text: 'Create compelling vision that aligns individual interests', weight: { visionary: 1, persuasive: 1, aligning: 1 } },
      { id: 'c', text: 'Establish clear governance and decision-making protocols', weight: { structured: 1, process: 1, governance: 1 } },
      { id: 'd', text: 'Demonstrate value through quick wins and visible progress', weight: { pragmatic: 1, results: 1, credibility: 1 } }
    ]
  },

  // EXECUTION DISCIPLINE EXTENSION
  {
    id: 'pro_exec_ext_1',
    phase: 'execution_discipline',
    type: 'forced_choice',
    question: 'When managing multiple priorities with competing deadlines, you typically:',
    subtitle: 'Select your natural approach',
    options: [
      { id: 'a', text: 'Prioritize ruthlessly and focus on highest impact items', weight: { prioritizing: 2, focused: 1, strategic: 1 } },
      { id: 'b', text: 'Work longer hours to ensure everything gets done', weight: { dedicated: 1, hardworking: 1, committed: 1 } },
      { id: 'c', text: 'Delegate more to free up capacity for critical items', weight: { delegating: 1, leveraging: 1, efficient: 1 } },
      { id: 'd', text: 'Renegotiate deadlines where possible to manage workload', weight: { negotiating: 1, realistic: 1, communicative: 1 } }
    ]
  },
  {
    id: 'pro_exec_ext_2',
    phase: 'execution_discipline',
    type: 'scenario',
    question: 'A project you are leading is behind schedule with a firm external deadline.',
    subtitle: 'What is your primary response?',
    options: [
      { id: 'a', text: 'Identify critical path items and focus resources there', weight: { analytical: 1, focused: 1, prioritizing: 1 } },
      { id: 'b', text: 'Add resources or extend hours to accelerate delivery', weight: { resourceful: 1, dedicated: 1, action: 1 } },
      { id: 'c', text: 'Descope to deliver core value on time', weight: { pragmatic: 2, negotiating: 1, realistic: 1 } },
      { id: 'd', text: 'Communicate proactively and reset stakeholder expectations', weight: { transparent: 1, communicative: 1, managing: 1 } }
    ]
  },
  {
    id: 'pro_exec_ext_3',
    phase: 'execution_discipline',
    type: 'situational_judgement',
    question: 'You notice a quality issue that could delay delivery but might not be caught by stakeholders.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would address the quality issue even if it means missing the deadline.',
    weights: {
      strongly_disagree: { expedient: 2, deadline_focused: 1 },
      disagree: { pragmatic: 1, balanced: 1 },
      agree: { quality: 1, principled: 1 },
      strongly_agree: { quality_focused: 2, integrity: 1, standards: 1 }
    }
  },
  {
    id: 'pro_exec_ext_4',
    phase: 'execution_discipline',
    type: 'reflection',
    question: 'Your approach to setting goals for yourself and your team is best described as:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Ambitious stretch goals that push beyond comfort zones', weight: { ambitious: 2, challenging: 1, growth: 1 } },
      { id: 'b', text: 'Realistic goals with high probability of achievement', weight: { realistic: 1, achievable: 1, reliable: 1 } },
      { id: 'c', text: 'Tiered goals with must-achieve baseline and stretch targets', weight: { structured: 1, layered: 1, balanced: 1 } },
      { id: 'd', text: 'Flexible goals that adapt based on emerging conditions', weight: { adaptive: 1, flexible: 1, responsive: 1 } }
    ]
  },
  {
    id: 'pro_exec_ext_5',
    phase: 'execution_discipline',
    type: 'forced_choice',
    question: 'When receiving feedback that your work needs improvement, you typically:',
    subtitle: 'Select your natural response',
    options: [
      { id: 'a', text: 'Ask clarifying questions to understand exactly what needs to change', weight: { analytical: 1, learning: 1, curious: 1 } },
      { id: 'b', text: 'Take it constructively and act immediately on improvements', weight: { action: 1, responsive: 1, growth: 1 } },
      { id: 'c', text: 'Reflect privately before responding or acting', weight: { reflective: 1, processing: 1, measured: 1 } },
      { id: 'd', text: 'Seek additional perspectives to validate the feedback', weight: { validating: 1, thorough: 1, multi_source: 1 } }
    ]
  },
  {
    id: 'pro_exec_ext_6',
    phase: 'execution_discipline',
    type: 'scenario',
    question: 'You realize that a process your team follows is inefficient but changing it would require significant effort.',
    subtitle: 'How do you approach this?',
    options: [
      { id: 'a', text: 'Champion the change despite the short-term disruption', weight: { change_agent: 2, improvement: 1, visionary: 1 } },
      { id: 'b', text: 'Propose incremental improvements that minimize disruption', weight: { pragmatic: 1, incremental: 1, measured: 1 } },
      { id: 'c', text: 'Wait for a natural break point to implement changes', weight: { timing: 1, strategic: 1, patient: 1 } },
      { id: 'd', text: 'Conduct cost-benefit analysis before deciding whether to change', weight: { analytical: 1, evidence: 1, rational: 1 } }
    ]
  },
  {
    id: 'pro_exec_ext_7',
    phase: 'execution_discipline',
    type: 'situational_judgement',
    question: 'A team member consistently delivers good work but always slightly late.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would address this pattern directly and set clear expectations about timeliness.',
    weights: {
      strongly_disagree: { permissive: 2, conflict_averse: 1 },
      disagree: { flexible: 1, understanding: 1 },
      agree: { standard_setting: 1, direct: 1 },
      strongly_agree: { accountable: 2, standards: 1, managing: 1 }
    }
  },
  {
    id: 'pro_exec_ext_8',
    phase: 'execution_discipline',
    type: 'forced_choice',
    question: 'Your personal approach to managing your energy and avoiding burnout is:',
    subtitle: 'Select your primary strategy',
    options: [
      { id: 'a', text: 'Clear boundaries between work and personal time', weight: { boundaries: 2, balance: 1, disciplined: 1 } },
      { id: 'b', text: 'Intense periods followed by recovery and reflection', weight: { sprinting: 1, recovery: 1, rhythmic: 1 } },
      { id: 'c', text: 'Regular exercise and wellness practices integrated into routine', weight: { wellness: 1, proactive: 1, healthy: 1 } },
      { id: 'd', text: 'Passion-driven work that energizes rather than depletes', weight: { passion: 1, intrinsic: 1, energized: 1 } }
    ]
  },
  {
    id: 'pro_exec_ext_9',
    phase: 'execution_discipline',
    type: 'scenario',
    question: 'You discover that you have been working on a task that is no longer needed due to changed priorities.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Accept it and pivot quickly to what matters now', weight: { adaptive: 2, pragmatic: 1, efficient: 1 } },
      { id: 'b', text: 'Feel frustrated by the wasted effort before moving on', weight: { processing: 1, human: 1, affected: 1 } },
      { id: 'c', text: 'Raise concern about communication that led to wasted effort', weight: { feedback: 1, improvement: 1, voice: 1 } },
      { id: 'd', text: 'See if any of the work can be repurposed elsewhere', weight: { resourceful: 1, salvaging: 1, creative: 1 } }
    ]
  },
  {
    id: 'pro_exec_ext_10',
    phase: 'execution_discipline',
    type: 'reflection',
    question: 'How do you typically handle tasks you find tedious or uninteresting?',
    subtitle: 'Select your natural approach',
    options: [
      { id: 'a', text: 'Complete them first to get them out of the way', weight: { disciplined: 2, efficient: 1, clearing: 1 } },
      { id: 'b', text: 'Find ways to make them more interesting or challenging', weight: { creative: 1, engaging: 1, reframing: 1 } },
      { id: 'c', text: 'Delegate them when possible', weight: { delegating: 1, leveraging: 1, strategic: 1 } },
      { id: 'd', text: 'Work through them but with lower energy than preferred tasks', weight: { honest: 1, human: 1, varied: 1 } }
    ]
  },

  // ADAPTABILITY & RESILIENCE EXTENSION
  {
    id: 'pro_adapt_ext_1',
    phase: 'adaptability',
    type: 'scenario',
    question: 'Your organization announces a major restructuring that significantly changes your role.',
    subtitle: 'What is your initial internal response?',
    options: [
      { id: 'a', text: 'See opportunity in the change and start positioning', weight: { opportunistic: 2, proactive: 1, positive: 1 } },
      { id: 'b', text: 'Take time to process before forming judgments', weight: { measured: 1, processing: 1, reflective: 1 } },
      { id: 'c', text: 'Assess impact on your career trajectory and options', weight: { analytical: 1, career: 1, strategic: 1 } },
      { id: 'd', text: 'Focus on how to help the team through the transition', weight: { others: 1, supportive: 1, leadership: 1 } }
    ]
  },
  {
    id: 'pro_adapt_ext_2',
    phase: 'adaptability',
    type: 'forced_choice',
    question: 'When entering a completely new domain or industry, you typically:',
    subtitle: 'Select your learning approach',
    options: [
      { id: 'a', text: 'Immerse yourself deeply until you develop real expertise', weight: { depth: 2, mastery: 1, thorough: 1 } },
      { id: 'b', text: 'Build enough working knowledge to be effective quickly', weight: { pragmatic: 1, efficient: 1, action: 1 } },
      { id: 'c', text: 'Find experts to learn from and partner with', weight: { collaborative: 1, leveraging: 1, humble: 1 } },
      { id: 'd', text: 'Apply frameworks from your experience to the new domain', weight: { transfer: 1, pattern: 1, confident: 1 } }
    ]
  },
  {
    id: 'pro_adapt_ext_3',
    phase: 'adaptability',
    type: 'situational_judgement',
    question: 'You receive feedback that directly contradicts your self-perception on a key capability.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would seriously consider that my self-perception might be inaccurate.',
    weights: {
      strongly_disagree: { defensive: 2, fixed: 1 },
      disagree: { resistant: 1, cautious: 1 },
      agree: { open: 1, reflective: 1 },
      strongly_agree: { humble: 2, growth_mindset: 1, self_aware: 1 }
    }
  },
  {
    id: 'pro_adapt_ext_4',
    phase: 'adaptability',
    type: 'reflection',
    question: 'When you fail at something important, your typical response pattern is:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Analyze what went wrong and create a plan to improve', weight: { analytical: 1, action: 1, learning: 1 } },
      { id: 'b', text: 'Take time to recover emotionally before analyzing', weight: { processing: 1, self_care: 1, emotional: 1 } },
      { id: 'c', text: 'Move on quickly without dwelling on the failure', weight: { resilient: 1, forward: 1, efficient: 1 } },
      { id: 'd', text: 'Seek feedback from others to understand what happened', weight: { collaborative: 1, learning: 1, humble: 1 } }
    ]
  },
  {
    id: 'pro_adapt_ext_5',
    phase: 'adaptability',
    type: 'scenario',
    question: 'A skill that was central to your career is becoming obsolete due to technological change.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Proactively learn the new technologies and skills', weight: { proactive: 2, learning: 1, future: 1 } },
      { id: 'b', text: 'Pivot to areas where your existing skills still have value', weight: { strategic: 1, pragmatic: 1, leveraging: 1 } },
      { id: 'c', text: 'Combine old expertise with new skills for unique positioning', weight: { creative: 1, differentiated: 1, synthesis: 1 } },
      { id: 'd', text: 'Transition to roles that are less susceptible to automation', weight: { strategic: 1, risk_averse: 1, protective: 1 } }
    ]
  },
  {
    id: 'pro_adapt_ext_6',
    phase: 'adaptability',
    type: 'forced_choice',
    question: 'Your relationship with ambiguity and uncertainty is best described as:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Comfortable - uncertainty often reveals opportunity', weight: { ambiguity_tolerant: 2, opportunistic: 1, confident: 1 } },
      { id: 'b', text: 'Manageable - I prefer clarity but can function without it', weight: { functional: 1, balanced: 1, adaptive: 1 } },
      { id: 'c', text: 'Challenging - I work to reduce uncertainty as quickly as possible', weight: { clarity_seeking: 1, anxious: 1, structured: 1 } },
      { id: 'd', text: 'Variable - depends on stakes and domain', weight: { contextual: 1, nuanced: 1, self_aware: 1 } }
    ]
  },
  {
    id: 'pro_adapt_ext_7',
    phase: 'adaptability',
    type: 'scenario',
    question: 'You are asked to lead an initiative in a domain where you have limited expertise.',
    subtitle: 'How do you approach this challenge?',
    options: [
      { id: 'a', text: 'Embrace it as a growth opportunity and dive in', weight: { courageous: 2, growth: 1, confident: 1 } },
      { id: 'b', text: 'Negotiate for resources and time to build required capability', weight: { pragmatic: 1, negotiating: 1, realistic: 1 } },
      { id: 'c', text: 'Surround yourself with domain experts to compensate', weight: { leveraging: 1, humble: 1, team: 1 } },
      { id: 'd', text: 'Be transparent about limitations while demonstrating learning agility', weight: { transparent: 1, humble: 1, authentic: 1 } }
    ]
  },
  {
    id: 'pro_adapt_ext_8',
    phase: 'adaptability',
    type: 'situational_judgement',
    question: 'A long-held belief you have about effective leadership is challenged by new research.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would update my beliefs and practices based on the new evidence.',
    weights: {
      strongly_disagree: { rigid: 2, closed: 1 },
      disagree: { skeptical: 1, cautious: 1 },
      agree: { open: 1, evidence: 1 },
      strongly_agree: { growth_mindset: 2, adaptive: 1, intellectual: 1 }
    }
  },
  {
    id: 'pro_adapt_ext_9',
    phase: 'adaptability',
    type: 'reflection',
    question: 'How do you typically respond when facing criticism of your work style or approach?',
    subtitle: 'Select your natural response',
    options: [
      { id: 'a', text: 'Consider it carefully and look for valid points', weight: { open: 2, reflective: 1, humble: 1 } },
      { id: 'b', text: 'Evaluate the source before deciding how much weight to give it', weight: { discerning: 1, analytical: 1, selective: 1 } },
      { id: 'c', text: 'Feel defensive initially but work through it', weight: { human: 1, processing: 1, growth: 1 } },
      { id: 'd', text: 'Remain confident in your approach unless evidence is strong', weight: { confident: 1, stable: 1, conviction: 1 } }
    ]
  },
  {
    id: 'pro_adapt_ext_10',
    phase: 'adaptability',
    type: 'forced_choice',
    question: 'When working with people who have very different styles or perspectives, you:',
    subtitle: 'Select your natural approach',
    options: [
      { id: 'a', text: 'Adapt your style to work more effectively with them', weight: { adaptive: 2, flexible: 1, accommodating: 1 } },
      { id: 'b', text: 'Find common ground while maintaining your core approach', weight: { balanced: 1, authentic: 1, bridging: 1 } },
      { id: 'c', text: 'Appreciate the diversity but may find collaboration challenging', weight: { honest: 1, aware: 1, struggling: 1 } },
      { id: 'd', text: 'Actively seek out these interactions as learning opportunities', weight: { curious: 1, growth: 1, diverse: 1 } }
    ]
  },

  // INTEGRITY & EQ EXTENSION
  {
    id: 'pro_integ_ext_1',
    phase: 'integrity_eq',
    type: 'scenario',
    question: 'You discover that your organization has been unknowingly engaging in a practice that is legal but ethically questionable.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Raise the concern through appropriate channels immediately', weight: { principled: 2, courageous: 1, voice: 1 } },
      { id: 'b', text: 'Research the issue thoroughly before deciding how to proceed', weight: { thorough: 1, measured: 1, analytical: 1 } },
      { id: 'c', text: 'Consider the implications of speaking up on your career', weight: { pragmatic: 1, realistic: 1, self_aware: 1 } },
      { id: 'd', text: 'Seek advice from trusted colleagues on how to handle it', weight: { collaborative: 1, wisdom_seeking: 1, careful: 1 } }
    ]
  },
  {
    id: 'pro_integ_ext_2',
    phase: 'integrity_eq',
    type: 'forced_choice',
    question: 'When your personal interests conflict with organizational interests, you typically:',
    subtitle: 'Select your natural tendency',
    options: [
      { id: 'a', text: 'Prioritize organizational interests as long as they are ethical', weight: { loyal: 1, principled: 1, organization: 1 } },
      { id: 'b', text: 'Seek creative solutions that serve both interests', weight: { creative: 1, win_win: 1, optimizing: 1 } },
      { id: 'c', text: 'Be transparent about the conflict and let others decide', weight: { transparent: 2, honest: 1, delegation: 1 } },
      { id: 'd', text: 'Recuse yourself from decisions where conflict exists', weight: { principled: 1, appropriate: 1, integrity: 1 } }
    ]
  },
  {
    id: 'pro_integ_ext_3',
    phase: 'integrity_eq',
    type: 'situational_judgement',
    question: 'A colleague shares confidential information that could help you but was shared inappropriately.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would not use the information and would address the boundary violation.',
    weights: {
      strongly_disagree: { opportunistic: 2, flexible_ethics: 1 },
      disagree: { pragmatic: 1, gray_area: 1 },
      agree: { principled: 1, boundary: 1 },
      strongly_agree: { integrity: 2, principled: 2, ethical: 1 }
    }
  },
  {
    id: 'pro_integ_ext_4',
    phase: 'integrity_eq',
    type: 'reflection',
    question: 'How do you typically handle situations where being completely honest might hurt someone?',
    subtitle: 'Select your natural approach',
    options: [
      { id: 'a', text: 'Prioritize honesty - they deserve the truth', weight: { honest: 2, direct: 1, truth: 1 } },
      { id: 'b', text: 'Be honest but focus on how you deliver the message', weight: { balanced: 1, empathetic: 1, diplomatic: 1 } },
      { id: 'c', text: 'Consider whether the truth serves their best interests', weight: { protective: 1, paternalistic: 1, caring: 1 } },
      { id: 'd', text: 'Find ways to guide them to the truth themselves', weight: { coaching: 1, facilitative: 1, developmental: 1 } }
    ]
  },
  {
    id: 'pro_integ_ext_5',
    phase: 'integrity_eq',
    type: 'scenario',
    question: 'You witness a respected leader treating a junior employee dismissively.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Address it directly with the leader afterward', weight: { courageous: 2, direct: 1, principled: 1 } },
      { id: 'b', text: 'Support the junior employee privately', weight: { supportive: 1, caring: 1, safe: 1 } },
      { id: 'c', text: 'Report it through appropriate HR or ethics channels', weight: { formal: 1, process: 1, proper: 1 } },
      { id: 'd', text: 'Observe if it is a pattern before taking action', weight: { measured: 1, evidence: 1, careful: 1 } }
    ]
  },
  {
    id: 'pro_integ_ext_6',
    phase: 'integrity_eq',
    type: 'forced_choice',
    question: 'Your approach to keeping commitments is best described as:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'I rarely commit unless certain I can deliver', weight: { careful: 1, reliable: 1, conservative: 1 } },
      { id: 'b', text: 'I commit readily and work hard to honor commitments', weight: { committed: 1, hardworking: 1, pleasing: 1 } },
      { id: 'c', text: 'I communicate early if circumstances change', weight: { transparent: 2, communicative: 1, realistic: 1 } },
      { id: 'd', text: 'My word is my bond - I will find a way to deliver', weight: { integrity: 2, determined: 1, reliable: 1 } }
    ]
  },
  {
    id: 'pro_integ_ext_7',
    phase: 'integrity_eq',
    type: 'scenario',
    question: 'A team member is underperforming due to personal issues they have shared with you confidentially.',
    subtitle: 'How do you handle this?',
    options: [
      { id: 'a', text: 'Maintain confidentiality while helping them get support', weight: { confidential: 2, supportive: 1, caring: 1 } },
      { id: 'b', text: 'Be transparent about performance while protecting privacy', weight: { balanced: 1, principled: 1, boundaried: 1 } },
      { id: 'c', text: 'Encourage them to share what they are comfortable sharing with relevant parties', weight: { empowering: 1, appropriate: 1, supportive: 1 } },
      { id: 'd', text: 'Address performance directly but offer flexible support options', weight: { direct: 1, compassionate: 1, pragmatic: 1 } }
    ]
  },
  {
    id: 'pro_integ_ext_8',
    phase: 'integrity_eq',
    type: 'situational_judgement',
    question: 'You realize you have made a mistake that no one else has noticed.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would proactively disclose the mistake rather than correct it quietly.',
    weights: {
      strongly_disagree: { self_protective: 2, hidden: 1 },
      disagree: { pragmatic: 1, efficient: 1 },
      agree: { transparent: 1, honest: 1 },
      strongly_agree: { integrity: 2, accountable: 2, courageous: 1 }
    }
  },
  {
    id: 'pro_integ_ext_9',
    phase: 'integrity_eq',
    type: 'reflection',
    question: 'How do you typically manage your own emotional reactions in challenging professional situations?',
    subtitle: 'Select your primary approach',
    options: [
      { id: 'a', text: 'Process internally and present a composed exterior', weight: { composed: 1, controlled: 1, professional: 1 } },
      { id: 'b', text: 'Be authentic about emotions while remaining professional', weight: { authentic: 2, balanced: 1, honest: 1 } },
      { id: 'c', text: 'Focus on the issue at hand rather than emotional reactions', weight: { task: 1, logical: 1, compartment: 1 } },
      { id: 'd', text: 'Take time to regulate before responding', weight: { regulated: 1, measured: 1, thoughtful: 1 } }
    ]
  },
  {
    id: 'pro_integ_ext_10',
    phase: 'integrity_eq',
    type: 'forced_choice',
    question: 'When you observe someone taking credit for work they did not do, you:',
    subtitle: 'Select your natural response',
    options: [
      { id: 'a', text: 'Address it directly with the person involved', weight: { direct: 2, courageous: 1, principled: 1 } },
      { id: 'b', text: 'Ensure the actual contributors are recognized appropriately', weight: { fair: 1, corrective: 1, just: 1 } },
      { id: 'c', text: 'Bring it up in a way that does not directly call out the behavior', weight: { diplomatic: 1, indirect: 1, preserving: 1 } },
      { id: 'd', text: 'Note it as information about that person but not necessarily intervene', weight: { observing: 1, strategic: 1, selective: 1 } }
    ]
  },

  // INNOVATION EXTENSION
  {
    id: 'pro_innov_ext_1',
    phase: 'innovation',
    type: 'scenario',
    question: 'Your industry is being disrupted by new entrants with fundamentally different business models.',
    subtitle: 'What is your strategic response?',
    options: [
      { id: 'a', text: 'Study the disruptors and identify ways to incorporate their innovations', weight: { adaptive: 1, learning: 1, pragmatic: 1 } },
      { id: 'b', text: 'Double down on your core strengths that disruptors cannot replicate', weight: { focused: 1, differentiated: 1, defensive: 1 } },
      { id: 'c', text: 'Launch your own disruptive initiatives even if they cannibalize existing business', weight: { bold: 2, self_disrupting: 1, visionary: 1 } },
      { id: 'd', text: 'Acquire or partner with disruptive players', weight: { strategic: 1, inorganic: 1, leveraging: 1 } }
    ]
  },
  {
    id: 'pro_innov_ext_2',
    phase: 'innovation',
    type: 'forced_choice',
    question: 'Your approach to exploring new ideas is best described as:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Wide exploration across many domains with selective deep dives', weight: { breadth: 1, curious: 1, selective: 1 } },
      { id: 'b', text: 'Deep expertise building in focused areas', weight: { depth: 2, specialist: 1, mastery: 1 } },
      { id: 'c', text: 'Connecting ideas across different fields for novel combinations', weight: { synthesis: 2, creative: 1, connecting: 1 } },
      { id: 'd', text: 'Practical experimentation to validate ideas quickly', weight: { experimental: 1, action: 1, pragmatic: 1 } }
    ]
  },
  {
    id: 'pro_innov_ext_3',
    phase: 'innovation',
    type: 'situational_judgement',
    question: 'A team member proposes an unconventional idea that initially sounds impractical.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would explore what could make it work before focusing on barriers.',
    weights: {
      strongly_disagree: { closed: 2, dismissive: 1 },
      disagree: { skeptical: 1, practical: 1 },
      agree: { open: 1, curious: 1 },
      strongly_agree: { innovative: 2, enabling: 1, exploratory: 1 }
    }
  },
  {
    id: 'pro_innov_ext_4',
    phase: 'innovation',
    type: 'reflection',
    question: 'How do you typically contribute to creative problem-solving sessions?',
    subtitle: 'Select your primary contribution style',
    options: [
      { id: 'a', text: 'Generate many ideas rapidly to explore possibilities', weight: { generative: 2, creative: 1, prolific: 1 } },
      { id: 'b', text: 'Build on and improve ideas others have proposed', weight: { collaborative: 1, refining: 1, building: 1 } },
      { id: 'c', text: 'Ask questions that reframe the problem differently', weight: { questioning: 1, reframing: 1, curious: 1 } },
      { id: 'd', text: 'Evaluate feasibility and help select best options', weight: { analytical: 1, practical: 1, judging: 1 } }
    ]
  },
  {
    id: 'pro_innov_ext_5',
    phase: 'innovation',
    type: 'scenario',
    question: 'You have an innovative idea but limited data to prove it will work.',
    subtitle: 'How do you proceed?',
    options: [
      { id: 'a', text: 'Design a small experiment to generate validating data', weight: { experimental: 2, evidence: 1, scientific: 1 } },
      { id: 'b', text: 'Build a compelling story and seek advocates for the idea', weight: { persuasive: 1, coalition: 1, storytelling: 1 } },
      { id: 'c', text: 'Find analogies from other domains to support the case', weight: { analogical: 1, creative: 1, reasoning: 1 } },
      { id: 'd', text: 'Trust your instinct and push forward with conviction', weight: { conviction: 1, bold: 1, intuitive: 1 } }
    ]
  },

  // ADDITIONAL DEPTH QUESTIONS
  {
    id: 'pro_depth_1',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'A board member privately questions a decision you made publicly. Their concerns have merit.',
    subtitle: 'How do you handle this?',
    options: [
      { id: 'a', text: 'Acknowledge privately and discuss how to address publicly', weight: { humble: 2, diplomatic: 1, wise: 1 } },
      { id: 'b', text: 'Revisit the decision openly with full transparency', weight: { transparent: 2, courageous: 1, integrity: 1 } },
      { id: 'c', text: 'Stand by the decision but commit to close monitoring', weight: { conviction: 1, accountable: 1, firm: 1 } },
      { id: 'd', text: 'Seek additional perspectives before deciding how to proceed', weight: { thorough: 1, consultative: 1, wise: 1 } }
    ]
  },
  {
    id: 'pro_depth_2',
    phase: 'leadership_influence',
    type: 'forced_choice',
    question: 'Your leadership style in crisis situations is best described as:',
    subtitle: 'Select the most accurate',
    options: [
      { id: 'a', text: 'Calm and reassuring - projecting stability', weight: { steady: 2, calming: 1, anchoring: 1 } },
      { id: 'b', text: 'Decisive and directive - taking charge', weight: { decisive: 2, commanding: 1, authoritative: 1 } },
      { id: 'c', text: 'Collaborative and inclusive - rallying the team', weight: { collaborative: 2, inclusive: 1, unifying: 1 } },
      { id: 'd', text: 'Analytical and systematic - addressing root causes', weight: { analytical: 2, systematic: 1, thorough: 1 } }
    ]
  },
  {
    id: 'pro_depth_3',
    phase: 'execution_discipline',
    type: 'situational_judgement',
    question: 'You consistently exceed expectations but at the cost of your team working excessive hours.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would recalibrate expectations even if it means lower short-term performance.',
    weights: {
      strongly_disagree: { driven: 2, short_term: 1 },
      disagree: { ambitious: 1, demanding: 1 },
      agree: { sustainable: 1, caring: 1 },
      strongly_agree: { sustainable: 2, people_first: 1, long_term: 1 }
    }
  },
  {
    id: 'pro_depth_4',
    phase: 'adaptability',
    type: 'scenario',
    question: 'New technology could automate parts of your own role within the organization.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Embrace it and focus on higher-value activities', weight: { adaptive: 2, forward: 1, growth: 1 } },
      { id: 'b', text: 'Learn the technology to stay relevant and lead adoption', weight: { proactive: 2, learning: 1, leading: 1 } },
      { id: 'c', text: 'Highlight the human elements that technology cannot replace', weight: { positioning: 1, differentiated: 1, defensive: 1 } },
      { id: 'd', text: 'Explore completely new directions where your skills translate', weight: { pivoting: 1, strategic: 1, exploring: 1 } }
    ]
  },
  {
    id: 'pro_depth_5',
    phase: 'integrity_eq',
    type: 'reflection',
    question: 'How do you handle situations where company policy conflicts with what you believe is right?',
    subtitle: 'Select your approach',
    options: [
      { id: 'a', text: 'Follow policy while working to change it through proper channels', weight: { compliant: 1, change_agent: 1, principled: 1 } },
      { id: 'b', text: 'Exercise judgment based on the spirit versus letter of policy', weight: { judgment: 2, nuanced: 1, wise: 1 } },
      { id: 'c', text: 'Escalate the conflict for guidance from leadership', weight: { transparent: 1, delegating: 1, appropriate: 1 } },
      { id: 'd', text: 'Prioritize doing right even if it means accepting consequences', weight: { principled: 2, courageous: 1, integrity: 1 } }
    ]
  },
  {
    id: 'pro_depth_6',
    phase: 'innovation',
    type: 'forced_choice',
    question: 'When a promising initiative fails, your primary focus is:',
    subtitle: 'Select your instinctive response',
    options: [
      { id: 'a', text: 'Understanding what went wrong to prevent recurrence', weight: { learning: 2, analytical: 1, preventive: 1 } },
      { id: 'b', text: 'Maintaining team morale and motivation', weight: { people: 2, supportive: 1, leadership: 1 } },
      { id: 'c', text: 'Identifying what can be salvaged or redirected', weight: { pragmatic: 2, resourceful: 1, optimizing: 1 } },
      { id: 'd', text: 'Moving quickly to the next opportunity', weight: { forward: 2, resilient: 1, action: 1 } }
    ]
  },
  {
    id: 'pro_depth_7',
    phase: 'strategic_decision',
    type: 'forced_choice',
    question: 'Your approach to competitive intelligence is:',
    subtitle: 'Select your primary method',
    options: [
      { id: 'a', text: 'Systematic monitoring and analysis of competitor actions', weight: { systematic: 2, analytical: 1, thorough: 1 } },
      { id: 'b', text: 'Industry networking and informal intelligence gathering', weight: { networked: 2, relational: 1, informal: 1 } },
      { id: 'c', text: 'Focus on customers rather than competitors', weight: { customer: 2, differentiated: 1, independent: 1 } },
      { id: 'd', text: 'Scenario planning around potential competitor moves', weight: { strategic: 2, anticipatory: 1, planning: 1 } }
    ]
  },
  {
    id: 'pro_depth_8',
    phase: 'leadership_influence',
    type: 'scenario',
    question: 'A direct report disagrees with your decision in a team meeting.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Welcome the dissent and explore their perspective publicly', weight: { open: 2, inclusive: 1, modeling: 1 } },
      { id: 'b', text: 'Acknowledge their view and offer to discuss after the meeting', weight: { diplomatic: 1, structured: 1, respectful: 1 } },
      { id: 'c', text: 'Explain your reasoning while remaining firm on the decision', weight: { decisive: 1, transparent: 1, explaining: 1 } },
      { id: 'd', text: 'Ask the team for their perspectives before responding', weight: { inclusive: 1, democratic: 1, facilitating: 1 } }
    ]
  },
  {
    id: 'pro_depth_9',
    phase: 'execution_discipline',
    type: 'reflection',
    question: 'How do you maintain focus when faced with attractive distractions from your core priorities?',
    subtitle: 'Select your primary strategy',
    options: [
      { id: 'a', text: 'Clear written priorities that I revisit regularly', weight: { structured: 2, disciplined: 1, systematic: 1 } },
      { id: 'b', text: 'Trusted advisors who help me stay accountable', weight: { accountable: 1, supported: 1, collaborative: 1 } },
      { id: 'c', text: 'Instinctive recognition of what truly matters', weight: { intuitive: 2, experienced: 1, wise: 1 } },
      { id: 'd', text: 'Evaluating opportunity cost of every major decision', weight: { analytical: 2, economic: 1, disciplined: 1 } }
    ]
  },
  {
    id: 'pro_depth_10',
    phase: 'adaptability',
    type: 'situational_judgement',
    question: 'You are offered a role that would accelerate your career but requires relocating away from family.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would seriously consider it despite the personal sacrifice.',
    weights: {
      strongly_disagree: { family_first: 2, bounded: 1 },
      disagree: { balanced: 1, values: 1 },
      agree: { ambitious: 1, open: 1 },
      strongly_agree: { career: 2, ambitious: 1, driven: 1 }
    }
  },
  {
    id: 'pro_depth_11',
    phase: 'integrity_eq',
    type: 'scenario',
    question: 'You learn that a peer is privately undermining your work while being friendly to your face.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Address it directly in a one-on-one conversation', weight: { direct: 2, courageous: 1, honest: 1 } },
      { id: 'b', text: 'Document instances and raise with leadership if pattern continues', weight: { strategic: 1, documented: 1, escalating: 1 } },
      { id: 'c', text: 'Focus on delivering excellent work and let results speak', weight: { performance: 1, above: 1, focused: 1 } },
      { id: 'd', text: 'Explore whether there is legitimate feedback driving their behavior', weight: { curious: 1, reflective: 1, humble: 1 } }
    ]
  },
  {
    id: 'pro_depth_12',
    phase: 'innovation',
    type: 'reflection',
    question: 'Your relationship with rules and established processes is:',
    subtitle: 'Select the most accurate',
    options: [
      { id: 'a', text: 'I follow them unless they clearly hinder good outcomes', weight: { pragmatic: 2, sensible: 1, balanced: 1 } },
      { id: 'b', text: 'I see them as guidelines that may need creative interpretation', weight: { flexible: 2, creative: 1, adaptive: 1 } },
      { id: 'c', text: 'I respect them as important for coordination and fairness', weight: { respectful: 1, compliant: 1, structured: 1 } },
      { id: 'd', text: 'I actively challenge rules that seem outdated or counterproductive', weight: { challenger: 2, change_agent: 1, bold: 1 } }
    ]
  },
  {
    id: 'pro_depth_13',
    phase: 'strategic_decision',
    type: 'scenario',
    question: 'Data supports one strategic direction but your experience suggests a different path.',
    subtitle: 'How do you reconcile this?',
    options: [
      { id: 'a', text: 'Trust the data unless you can articulate specific concerns', weight: { evidence: 2, humble: 1, analytical: 1 } },
      { id: 'b', text: 'Trust your experience but use data to refine the approach', weight: { experiential: 2, synthesizing: 1, balanced: 1 } },
      { id: 'c', text: 'Explore whether the data and experience are measuring different things', weight: { curious: 2, reconciling: 1, thorough: 1 } },
      { id: 'd', text: 'Design a test that could validate one perspective over the other', weight: { experimental: 2, scientific: 1, rigorous: 1 } }
    ]
  },
  {
    id: 'pro_depth_14',
    phase: 'leadership_influence',
    type: 'forced_choice',
    question: 'How do you build credibility with a new team that did not choose you as their leader?',
    subtitle: 'Select your primary approach',
    options: [
      { id: 'a', text: 'Listen deeply to understand their context before making changes', weight: { listening: 2, humble: 1, patient: 1 } },
      { id: 'b', text: 'Deliver quick wins that demonstrate competence', weight: { results: 2, credibility: 1, proving: 1 } },
      { id: 'c', text: 'Be authentic and transparent about your intentions', weight: { authentic: 2, transparent: 1, honest: 1 } },
      { id: 'd', text: 'Build individual relationships through one-on-ones', weight: { relational: 2, personal: 1, connecting: 1 } }
    ]
  },
  {
    id: 'pro_depth_15',
    phase: 'execution_discipline',
    type: 'situational_judgement',
    question: 'A colleague whose work you depend on is consistently late, affecting your deliverables.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would address this directly with them before escalating or working around them.',
    weights: {
      strongly_disagree: { avoiding: 2, passive: 1 },
      disagree: { indirect: 1, working_around: 1 },
      agree: { direct: 1, professional: 1 },
      strongly_agree: { direct: 2, accountable: 1, professional: 1 }
    }
  }
];

// Weight mapping to LeadEconomics Core Dimensions
export const DIMENSION_MAPPING = {
  strategic_thinking: ['analytical', 'strategic_thinking', 'visionary', 'long_term', 'pattern_recognition'],
  decision_quality: ['decision_velocity', 'validation_discipline', 'structured_thinking', 'measured_risk', 'judgment'],
  leadership_influence: ['influence', 'inspirational', 'executive_presence', 'coalition', 'stakeholder_mgmt'],
  relationship_orientation: ['empathy', 'collaborative', 'trust_building', 'supportive', 'inclusion'],
  financial_awareness: ['prudence', 'resourceful', 'analytical', 'risk_aware'],
  execution_discipline: ['execution_intensity', 'accountability', 'focus', 'systematic', 'results'],
  problem_solving: ['analytical', 'creative', 'root_cause', 'solution_focused', 'resourceful'],
  adaptability: ['adaptability', 'learning_agility', 'flexibility', 'resilience', 'growth_mindset'],
  integrity: ['integrity', 'transparent', 'accountable', 'principled', 'values_driven'],
  communication: ['direct', 'transparent', 'diplomatic', 'storytelling', 'listening']
};

// Question count configuration
export const PROFESSIONAL_CONFIG = {
  totalQuestions: 85,
  timeMinutes: 30,
  phases: {
    strategic_decision: { min: 15, max: 20 },
    leadership_influence: { min: 15, max: 20 },
    execution_discipline: { min: 15, max: 20 },
    adaptability: { min: 12, max: 15 },
    integrity_eq: { min: 12, max: 15 },
    innovation: { min: 8, max: 12 }
  }
};
